import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import { toast } from 'react-toastify'
import AddRoundedIcon          from '@mui/icons-material/AddRounded'
import EngineeringRoundedIcon  from '@mui/icons-material/EngineeringRounded'
import EditRoundedIcon         from '@mui/icons-material/EditRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import DescriptionRoundedIcon  from '@mui/icons-material/DescriptionRounded'
import DeleteRoundedIcon       from '@mui/icons-material/DeleteRounded'
import DownloadRoundedIcon     from '@mui/icons-material/DownloadRounded'
import HistoryRoundedIcon      from '@mui/icons-material/HistoryRounded'
import BlockRoundedIcon        from '@mui/icons-material/BlockRounded'
import CloseRoundedIcon        from '@mui/icons-material/CloseRounded'
import ReceiptLongRoundedIcon  from '@mui/icons-material/ReceiptLongRounded'
import AutoAwesomeRoundedIcon  from '@mui/icons-material/AutoAwesomeRounded'
import OpenInNewRoundedIcon    from '@mui/icons-material/OpenInNewRounded'
import InfoOutlinedIcon        from '@mui/icons-material/InfoOutlined'
import ExpandMoreRoundedIcon    from '@mui/icons-material/ExpandMoreRounded'
import SearchRoundedIcon        from '@mui/icons-material/SearchRounded'
import FolderRoundedIcon        from '@mui/icons-material/FolderRounded'
import ChevronRightRoundedIcon  from '@mui/icons-material/ChevronRightRounded'
import CallMergeRoundedIcon      from '@mui/icons-material/CallMergeRounded'
import PostAddRoundedIcon         from '@mui/icons-material/PostAddRounded'
import {
  listContractors, getContractorById, createContractor, updateContractor, deleteContractor,
  addContractorDocument, deleteContractorDocument,
  downloadContractorDocument, uploadContractorFile, extractContractorDetails,
  getContractorExtractStatus, listContractorActivity, undoContractorMerge,
  listContractorApprovers, extractContractorInsuranceValue,
  listPurchaseOrders, createPurchaseOrder, updatePurchaseOrder, deletePurchaseOrder,
  listUnassignedPurchaseOrders,
} from 'services/contractorService'
import { hideEmail } from 'utils/user'

const approverName = u => {
  const n = [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
  return n || (u.email ? hideEmail(u.email) : 'Unknown')
}

const formatMoney = v => (v == null ? null : `£${Number(v).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`)

const formatShortDate = iso => {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}
import { listContracts } from 'services/contractService'
import { fetchUserBps } from 'services/BpService'
import { getFilesListForBp } from 'services/fileService'
import { fetchBpUnits } from 'services/unitsService'
import { VETTING_META, DOC_TINT } from 'features/contractors/contractorMeta'
import GuruInsightModal from 'features/bpDetails/Components/files/GuruInsightModal'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import MergeContractorsModal from 'features/contractors/MergeContractorsModal'
import BulkAddContractorsModal from 'features/contractors/BulkAddContractorsModal'
import SearchableSelect from 'components/SearchableSelect'
import { RAG } from 'features/dashboard/charts/scoreColour'

const toLocalInput = iso => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const ContractorsPage = () => {
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [contractors, setContractors] = useState([])
  const [meta, setMeta] = useState(null)
  const [docTypes, setDocTypes] = useState({})
  const [editing, setEditing] = useState(null)
  const [docTarget, setDocTarget] = useState(null)
  const [folderTarget, setFolderTarget] = useState(null)
  const [deepLinkedContractor, setDeepLinkedContractor] = useState(null)
  const [activityTarget, setActivityTarget] = useState(null)
  const [poTarget, setPoTarget] = useState(null)
  const [unassignedPos, setUnassignedPos] = useState([])
  const [showUnassigned, setShowUnassigned] = useState(false)
  const [showMerge, setShowMerge] = useState(false)
  const [showBulkAdd, setShowBulkAdd] = useState(false)
  const [vettingStatuses, setVettingStatuses] = useState({})
  const [buildings, setBuildings] = useState([])
  const [trades, setTrades] = useState([])
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [vettingFilter, setVettingFilter] = useState('all')
  const [activeFilter, setActiveFilter] = useState('all')
  const [sourceFilter, setSourceFilter] = useState('all')
  const [tradeFilter, setTradeFilter] = useState('all')
  const [attentionFilter, setAttentionFilter] = useState('all')
  const [buildingFilter, setBuildingFilter] = useState('all')
  const [sort, setSort] = useState('name')
  const [insightFile, setInsightFile] = useState(null)

  const [searchParams] = useSearchParams()
  const deepLinkContractorId = searchParams.get('contractor')
  const deepLinkAppliedRef = useRef(false)

  const filtersActive = search.trim() !== '' || vettingFilter !== 'all' || activeFilter !== 'all'
    || sourceFilter !== 'all' || tradeFilter !== 'all' || attentionFilter !== 'all' || buildingFilter !== 'all'

  const clearFilters = () => {
    setSearch(''); setVettingFilter('all'); setActiveFilter('all')
    setSourceFilter('all'); setTradeFilter('all'); setAttentionFilter('all'); setBuildingFilter('all')
  }

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(t)
  }, [search])

  const loadSeqRef = useRef(0)

  const buildParams = useCallback(page => {
    const params = { page, per_page: 25, sort }
    if (debouncedSearch) params['filter[search]'] = debouncedSearch
    if (vettingFilter !== 'all') params['filter[vetting_status]'] = vettingFilter
    if (activeFilter !== 'all') params['filter[is_active]'] = activeFilter
    if (sourceFilter !== 'all') params['filter[source]'] = sourceFilter
    if (tradeFilter !== 'all') params['filter[trade]'] = tradeFilter
    if (attentionFilter !== 'all') params['filter[attention]'] = attentionFilter
    if (buildingFilter !== 'all') params['filter[building_id]'] = buildingFilter
    return params
  }, [debouncedSearch, vettingFilter, activeFilter, sourceFilter, tradeFilter, attentionFilter, buildingFilter, sort])

  const load = useCallback(page => {
    const targetPage = page || 1
    const seq = ++loadSeqRef.current
    if (targetPage > 1) setLoadingMore(true)
    else setLoading(true)
    listContractors(buildParams(targetPage))
      .then(res => {
        if (seq !== loadSeqRef.current) return
        const payload = res?.data?.data || {}
        const nextMeta = res?.data?.meta || null
        const incoming = payload.contractors || []
        setMeta(nextMeta)
        setContractors(prev => (nextMeta?.currentPage ?? 1) > 1
          ? [...prev, ...incoming.filter(i => !prev.some(r => r.id === i.id))]
          : incoming)
        if ((nextMeta?.currentPage ?? 1) === 1) {
          setDocTypes(payload.docTypes || {})
          setVettingStatuses(payload.vettingStatuses || {})
          setBuildings(payload.buildings || [])
          setTrades(payload.trades || [])
        }
      })
      .catch(() => { if (seq === loadSeqRef.current) toast.error('Could not load contractors.') })
      .finally(() => { if (seq === loadSeqRef.current) { setLoading(false); setLoadingMore(false) } })
  }, [buildParams])

  const loadedPages = meta?.currentPage || 1
  const refreshLoaded = useCallback(async () => {
    const pages = Math.max(1, loadedPages)
    const seq = ++loadSeqRef.current
    try {
      const results = await Promise.all(Array.from({ length: pages }, (_, i) => listContractors(buildParams(i + 1))))
      if (seq !== loadSeqRef.current) return
      const rows = []
      const seen = new Set()
      results.forEach(res => (res?.data?.data?.contractors || []).forEach(c => {
        if (seen.has(c.id)) return
        seen.add(c.id)
        rows.push(c)
      }))
      const lastMeta = results[results.length - 1]?.data?.meta || null
      setContractors(rows)
      if (lastMeta) setMeta({ ...lastMeta, currentPage: Math.min(pages, lastMeta.lastPage ?? pages) })
    } catch (e) {
      if (seq === loadSeqRef.current) toast.error('Could not refresh contractors.')
    }
  }, [loadedPages, buildParams])

  const refreshDeepLinked = useCallback(contractorId => {
    if (!deepLinkedContractor || deepLinkedContractor.id !== contractorId) return
    getContractorById(contractorId)
      .then(fetched => setDeepLinkedContractor(prev => (prev?.id === contractorId ? fetched : prev)))
      .catch(() => {})
  }, [deepLinkedContractor])

  const loadUnassigned = useCallback(() => {
    listUnassignedPurchaseOrders()
      .then(res => setUnassignedPos(res?.data?.data || []))
      .catch(() => {})
  }, [])

  useEffect(() => { load(1) }, [load])
  useEffect(() => { loadUnassigned() }, [loadUnassigned])

  useEffect(() => {
    if (!deepLinkContractorId || deepLinkAppliedRef.current || loading) return
    deepLinkAppliedRef.current = true
    const match = contractors.find(c => String(c.id) === String(deepLinkContractorId))
    if (match) {
      setFolderTarget(match.id)
      return
    }
    getContractorById(deepLinkContractorId)
      .then(fetched => {
        if (!fetched) return
        setDeepLinkedContractor(fetched)
        setFolderTarget(fetched.id)
      })
      .catch(() => {})
  }, [deepLinkContractorId, loading, contractors])

  const saveContractor = async (draft, pendingFile) => {
    try {
      if (draft.id) {
        const res = await updateContractor(draft.id, draft)
        const updated = res?.data?.data || {}
        toast.success('Contractor updated.')
        setContractors(prev => prev.map(x => (x.id === draft.id ? { ...x, ...updated } : x)))
        setDeepLinkedContractor(prev => (prev?.id === draft.id ? { ...prev, ...updated } : prev))
        setEditing(null)
        refreshLoaded()
      } else {
        const res = await createContractor(draft)
        const contractorId = res?.data?.data?.id
        toast.success('Contractor added.')
        if (pendingFile && contractorId) {
          try {
            const up = await uploadContractorFile(contractorId, pendingFile)
            await addContractorDocument(contractorId, {
              doc_type: 'other',
              display_name: pendingFile.name,
              s3_key: up.s3Key,
              mime_type: up.mimeType,
              size_bytes: up.sizeBytes,
            })
          } catch (err) {
            toast.info('Contractor saved. You can attach the document from the contractor card.')
          }
        }
        setEditing(null)
        await load()
      }
    } catch (e) {
      toast.error('Save failed.')
    }
  }

  const handleDeleteContractor = async (c) => {
    const label = c.name || c.company || 'this contractor'
    if (!window.confirm(`Delete ${label}? They won't be re-added by document extraction, even if the same name appears again.`)) return
    try {
      await deleteContractor(c.id)
      const wasListed = contractors.some(x => x.id === c.id)
      setContractors(prev => prev.filter(x => x.id !== c.id))
      if (wasListed) setMeta(prev => (prev?.total != null ? { ...prev, total: Math.max(0, prev.total - 1) } : prev))
      setDeepLinkedContractor(prev => (prev?.id === c.id ? null : prev))
      setFolderTarget(prev => (prev === c.id ? null : prev))
      toast.success('Contractor deleted.')
    } catch (e) {
      toast.error('Could not delete this contractor. Please try again.')
    }
  }

  const toggleActive = async c => {
    const next = !c.isActive
    setContractors(prev => prev.map(x => (x.id === c.id ? { ...x, isActive: next } : x)))
    try {
      await updateContractor(c.id, { is_active: next })
      toast.success(next ? `${c.name} set to active` : `${c.name} set to inactive`)
      if (filtersActive) refreshLoaded()
    } catch (e) {
      setContractors(prev => prev.map(x => (x.id === c.id ? { ...x, isActive: !next } : x)))
      toast.error('Could not update status.')
    }
  }

  const changeVetting = async (c, status) => {
    if (!status || status === c.vettingStatus) return
    const prev = c.vettingStatus
    setContractors(list => list.map(x => (x.id === c.id ? { ...x, vettingStatus: status } : x)))
    try {
      await updateContractor(c.id, { vetting_status: status })
      toast.success(`Vetting status set to “${VETTING_META[status]?.label || status}”.`)
      if (filtersActive) refreshLoaded()
    } catch (e) {
      setContractors(list => list.map(x => (x.id === c.id ? { ...x, vettingStatus: prev } : x)))
      toast.error('Could not update vetting status.')
    }
  }

  const saveDocument = async draft => {
    try {
      await addContractorDocument(docTarget.id, draft)
      toast.success('Document recorded.')
      refreshDeepLinked(docTarget.id)
      setDocTarget(null)
      await refreshLoaded()
    } catch (e) {
      toast.error('Could not add document.')
    }
  }

  const removeDocument = async (contractor, doc) => {
    if (!window.confirm(`Remove ${doc.displayName}?`)) return
    try {
      await deleteContractorDocument(contractor.id, doc.id)
      toast.success('Document removed.')
      refreshDeepLinked(contractor.id)
      await refreshLoaded()
    } catch (e) {
      toast.error('Could not remove document.')
    }
  }

  const openDocument = async (contractor, doc) => {
    try {
      const res = await downloadContractorDocument(contractor.id, doc.id)
      const url = res?.data?.data?.downloadUrl
      if (url) window.open(url, '_blank', 'noopener')
    } catch (e) {
      toast.error('Could not fetch the file.')
    }
  }

  const openContractorDoc = (contractor, doc) => {
    if (doc.fileId) {
      setInsightFile({ id: doc.fileId, displayName: doc.displayName, buildingPassportId: doc.linkedBpId })
    } else if (doc.hasFile) {
      openDocument(contractor, doc)
    }
  }

  const folderContractor = folderTarget
    ? (contractors.find(c => c.id === folderTarget)
      || (deepLinkedContractor && deepLinkedContractor.id === folderTarget ? deepLinkedContractor : null))
    : null

  return (
    <Page>
      <Head>
        <HeadText>
          <Eyebrow>Supply chain</Eyebrow>
          <Title>Contractors</Title>
          <Subtitle>
            Competence, insurance and RAMS records with expiry tracking, plus on-time performance from their task history.
          </Subtitle>
        </HeadText>
        <HeadActions>
          <MergeBtn onClick={() => setShowBulkAdd(true)}>
            <PostAddRoundedIcon style={{ fontSize: 16 }} />
            Bulk add
          </MergeBtn>
          <MergeBtn onClick={() => setShowMerge(true)}>
            <CallMergeRoundedIcon style={{ fontSize: 16 }} />
            Merge duplicates
          </MergeBtn>
          <PrimaryBtn onClick={() => setEditing({ name: '', company: '', email: '', phone: '', trade: '', serviceScope: '', notes: '' })} data-tour-id='contractors.add-btn'>
            <AddRoundedIcon style={{ fontSize: 16 }} />
            Add contractor
          </PrimaryBtn>
        </HeadActions>
      </Head>

      <MergeContractorsModal open={showMerge} onClose={() => setShowMerge(false)} onMerged={() => load(1)} />
      <BulkAddContractorsModal open={showBulkAdd} onClose={() => setShowBulkAdd(false)} onAdded={() => load(1)} />

      {unassignedPos.length > 0 && (
        <UnassignedBanner onClick={() => setShowUnassigned(true)}>
          <UnassignedIcon><ReceiptLongRoundedIcon style={{ fontSize: 16 }} /></UnassignedIcon>
          <UnassignedText>
            <strong>{unassignedPos.length} purchase order{unassignedPos.length === 1 ? '' : 's'}</strong> raised on tasks {unassignedPos.length === 1 ? 'isn’t' : 'aren’t'} linked to a contractor yet.
          </UnassignedText>
          <UnassignedBtn>Review &amp; assign</UnassignedBtn>
        </UnassignedBanner>
      )}

      {(contractors.length > 0 || filtersActive) && (
        <FilterBar>
          <SearchInput placeholder='Search name, company, trade, email…' aria-label='Search contractors' value={search} onChange={e => setSearch(e.target.value)} />
          <FilterWrap>
            <SearchableSelect
              value={vettingFilter}
              onChange={v => setVettingFilter(v)}
              options={[
                { value: 'all', label: 'All vetting' },
                { value: 'approved', label: 'Approved', color: VETTING_META.approved?.tint },
                { value: 'pending_review', label: 'Pending review', color: VETTING_META.pending_review?.tint },
                { value: 'probationary', label: 'Probationary', color: VETTING_META.probationary?.tint },
                { value: 'suspended', label: 'Suspended', color: VETTING_META.suspended?.tint },
              ]}
              ariaLabel='Filter by vetting status'
            />
          </FilterWrap>
          <FilterWrap>
            <SearchableSelect
              value={activeFilter}
              onChange={v => setActiveFilter(v)}
              options={[
                { value: 'all', label: 'Active & inactive' },
                { value: 'active', label: 'Active only' },
                { value: 'inactive', label: 'Inactive only' },
              ]}
              ariaLabel='Filter by active status'
            />
          </FilterWrap>
          <FilterWrap>
            <SearchableSelect
              value={sourceFilter}
              onChange={v => setSourceFilter(v)}
              options={[
                { value: 'all', label: 'All sources' },
                { value: 'ai_extracted', label: 'Found in documents' },
                { value: 'manual', label: 'Added manually' },
              ]}
              ariaLabel='Filter by source'
            />
          </FilterWrap>
          {trades.length > 0 && (
            <FilterWrap>
              <SearchableSelect
                value={tradeFilter}
                onChange={v => setTradeFilter(v)}
                options={[{ value: 'all', label: 'All trades' }, ...trades.map(t => ({ value: t, label: t }))]}
                searchPlaceholder='Search trades…'
                ariaLabel='Filter by trade'
              />
            </FilterWrap>
          )}
          {buildings.length > 0 && (
            <FilterWrap>
              <SearchableSelect
                value={buildingFilter}
                onChange={v => setBuildingFilter(v)}
                options={[{ value: 'all', label: 'All buildings' }, ...buildings.map(b => ({ value: b.id, label: b.name }))]}
                searchPlaceholder='Search buildings…'
                ariaLabel='Filter contractors by building'
              />
            </FilterWrap>
          )}
          <FilterWrap>
            <SearchableSelect
              value={attentionFilter}
              onChange={v => setAttentionFilter(v)}
              options={[
                { value: 'all', label: 'Any status' },
                { value: 'attention', label: 'Needs attention', color: RAG.critical.fg },
                { value: 'ok', label: 'No issues' },
              ]}
              ariaLabel='Filter by attention'
            />
          </FilterWrap>
          <FilterWrap>
            <SearchableSelect
              value={sort}
              onChange={v => setSort(v)}
              options={[
                { value: 'name', label: 'Sort: Name' },
                { value: 'recent', label: 'Sort: Most recent' },
              ]}
              ariaLabel='Sort contractors'
            />
          </FilterWrap>
          {filtersActive && <ClearBtn type='button' onClick={clearFilters}>Clear filters</ClearBtn>}
          <Count>Showing {contractors.length.toLocaleString('en-GB')} of {(meta?.total ?? contractors.length).toLocaleString('en-GB')} {(meta?.total ?? contractors.length) === 1 ? 'contractor' : 'contractors'}</Count>
        </FilterBar>
      )}

      {loading ? (
        <Grid>
          {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
        </Grid>
      ) : contractors.length === 0 ? (
        filtersActive ? (
          <Empty><p>No contractors match your search or filters.</p></Empty>
        ) : (
          <Empty>
            <EngineeringRoundedIcon style={{ fontSize: 36, color: '#cbcbd2' }} />
            <p>No contractors yet. Add your supply chain to track their insurance, competence and performance in one place.</p>
          </Empty>
        )
      ) : (
        <>
        <Grid>
          {contractors.map(c => {
            const perf = c.performance
            return (
              <Card key={c.id} $inactive={!c.isActive}>
                <CardTop>
                  <CardIdent>
                    <CardName>{c.name}</CardName>
                    {c.company && <CardCompany>{c.company}</CardCompany>}
                    {c.source === 'ai_extracted' && c.vettingStatus === 'pending_review' && (
                      <NewlyIdentifiedPill title='Automatically identified from your documents and not yet reviewed. Open the card to check the details, then set a vetting status.'>
                        <AutoAwesomeRoundedIcon style={{ fontSize: 12 }} />
                        Newly identified
                      </NewlyIdentifiedPill>
                    )}
                    {c.trade && <TradeChip>{c.trade}</TradeChip>}
                    <StatusRow>
                      <StatusField>
                        <StatusCaption>In use</StatusCaption>
                        <StatusToggle
                          $active={c.isActive}
                          onClick={() => toggleActive(c)}
                          title={c.isActive
                            ? 'In use. This supplier is in your live supply chain. Click to park them.'
                            : 'Not in use (parked). Click to add them back to your supply chain.'}
                        >
                          <StatusDot $active={c.isActive} />
                          {c.isActive ? 'Active' : 'Inactive'}
                        </StatusToggle>
                      </StatusField>
                      <StatusField>
                        <StatusCaption>Vetting</StatusCaption>
                        <VettingControl contractor={c} statuses={vettingStatuses} onChange={changeVetting} />
                      </StatusField>
                      <StatusExplainer />
                    </StatusRow>
                  </CardIdent>
                  <CardActions>
                    <EditBtn onClick={() => setEditing({ ...c })} title='Edit this contractor’s details'>
                      <EditRoundedIcon style={{ fontSize: 15 }} />
                      Edit
                    </EditBtn>
                    <DeleteContractorBtn onClick={() => handleDeleteContractor(c)} title='Delete this contractor'>
                      <DeleteRoundedIcon style={{ fontSize: 15 }} />
                      Delete
                    </DeleteContractorBtn>
                  </CardActions>
                </CardTop>

                {c.source === 'ai_extracted' && (
                  <SourceStrip
                    as={c.sourceFileId && c.sourceBpId ? 'button' : 'div'}
                    $clickable={!!(c.sourceFileId && c.sourceBpId)}
                    onClick={c.sourceFileId && c.sourceBpId
                      ? () => setInsightFile({ id: c.sourceFileId, displayName: c.sourceFileName, buildingPassportId: c.sourceBpId })
                      : undefined}
                    title={c.sourceFileName
                      ? `Auto-detected from "${c.sourceFileName}". View the AI insight`
                      : 'Automatically detected from an uploaded document'}
                  >
                    <AutoAwesomeRoundedIcon style={{ fontSize: 14 }} />
                    <SourceStripText>
                      <strong>Auto-detected</strong>
                      {c.sourceFileName ? <> from <em>{c.sourceFileName}</em></> : <> from an uploaded document</>}
                      {c.sourceBpName && <> · <strong>{c.sourceBpName}</strong></>}
                    </SourceStripText>
                    {c.sourceIsRecent && <RecentTag title='Recently detected. Active while you review it'>New</RecentTag>}
                    {c.sourceFileId && c.sourceBpId && <OpenInNewRoundedIcon style={{ fontSize: 13 }} />}
                  </SourceStrip>
                )}

                {!c.isActive && c.source === 'ai_extracted' && !c.isActiveOverridden && (
                  <PendingHint>
                    <InfoOutlinedIcon style={{ fontSize: 13 }} />
                    <span>Inactive. Auto-detected and not yet reviewed. Set it <strong>Active</strong> to add it to your supply chain.</span>
                  </PendingHint>
                )}

                {c.assignmentBlocked && (
                  <BlockedStrip>
                    <BlockRoundedIcon style={{ fontSize: 13 }} />
                    <BlockedBody>
                      <strong>Assignment blocked</strong>
                      {(c.blockedReasons || []).map((r, i) => <span key={i}>{r}</span>)}
                    </BlockedBody>
                  </BlockedStrip>
                )}
                {!c.assignmentBlocked && (c.expiringDocs || 0) > 0 && (
                  <AlertStrip>
                    <WarningAmberRoundedIcon style={{ fontSize: 13 }} />
                    {c.expiringDocs} document{c.expiringDocs === 1 ? '' : 's'} expiring within 30 days
                  </AlertStrip>
                )}

                <FolderTile type='button' onClick={() => setFolderTarget(c.id)}
                  title='Open this contractor’s document folder'>
                  <FolderTileIcon><FolderRoundedIcon style={{ fontSize: 18 }} /></FolderTileIcon>
                  <FolderTileText>
                    <FolderTileTitle>Documents</FolderTileTitle>
                    <FolderTileMeta>
                      {(c.documents || []).length === 0
                        ? 'No documents yet. Tap to link one'
                        : `${c.documents.length} document${c.documents.length === 1 ? '' : 's'}`}
                    </FolderTileMeta>
                  </FolderTileText>
                  <FolderTileRight>
                    {(c.expiredDocs || 0) > 0 && (
                      <FolderChip $bg='#fdecea' $fg='#b00020'>{c.expiredDocs} expired</FolderChip>
                    )}
                    {(c.expiringDocs || 0) > 0 && (
                      <FolderChip $bg='#fef3c7' $fg='#d97706'>{c.expiringDocs} expiring</FolderChip>
                    )}
                    <ChevronRightRoundedIcon style={{ fontSize: 18, color: '#c4c9d4' }} />
                  </FolderTileRight>
                </FolderTile>

                {(c.publicLiabilityValue != null || c.contractStartDate || c.nextReviewDate || c.approvedBy?.name) && (
                  <VettingDetails>
                    {c.publicLiabilityValue != null && (
                      <VettingItem><VettingKey>Public liability</VettingKey><VettingVal>{formatMoney(c.publicLiabilityValue)}</VettingVal></VettingItem>
                    )}
                    {c.contractStartDate && (
                      <VettingItem>
                        <VettingKey>Contract</VettingKey>
                        <VettingVal>
                          {formatShortDate(c.contractStartDate)}
                          {c.contractEndDate ? ` – ${formatShortDate(c.contractEndDate)}` : ''}
                          {c.contractTermMonths ? ` · ${c.contractTermMonths}m term` : ''}
                        </VettingVal>
                      </VettingItem>
                    )}
                    {c.nextReviewDate && (
                      <VettingItem>
                        <VettingKey>Next review</VettingKey>
                        <VettingVal>
                          {formatShortDate(c.nextReviewDate)}
                          {c.reviewCadenceMonths ? ` · every ${c.reviewCadenceMonths}m` : ''}
                        </VettingVal>
                      </VettingItem>
                    )}
                    {c.approvedBy?.name && (
                      <VettingItem><VettingKey>Approved by</VettingKey><VettingVal>{c.approvedBy.name}</VettingVal></VettingItem>
                    )}
                  </VettingDetails>
                )}

                <CardFoot>
                  {perf ? (
                    <PerfRow>
                      <PerfStat title='Tasks ever assigned to this contractor'>
                        <PerfValue>{perf.assigned}</PerfValue>
                        <PerfLabel>tasks</PerfLabel>
                      </PerfStat>
                      <PerfStat title='Tasks completed'>
                        <PerfValue>{perf.completed ?? 0}</PerfValue>
                        <PerfLabel>completed</PerfLabel>
                      </PerfStat>
                      <PerfStat title='Share of completed tasks finished on or before the deadline'>
                        <PerfValue $tint={perf.onTimeRate === null ? undefined : perf.onTimeRate >= 80 ? '#2aa6b3' : perf.onTimeRate >= 50 ? '#d97706' : '#b00020'}>
                          {perf.onTimeRate === null ? 'No completed tasks yet' : `${Math.round(perf.onTimeRate)}%`}
                        </PerfValue>
                        <PerfLabel>on time</PerfLabel>
                      </PerfStat>
                      <PerfStat title='Completed after the deadline'>
                        <PerfValue $tint={(perf.completedLate || 0) > 0 ? '#d97706' : undefined}>{perf.completedLate || 0}</PerfValue>
                        <PerfLabel>late</PerfLabel>
                      </PerfStat>
                      <PerfStat title='Not done and past the deadline'>
                        <PerfValue $tint={(perf.overdue || 0) > 0 ? '#b00020' : undefined}>{perf.overdue || 0}</PerfValue>
                        <PerfLabel>overdue</PerfLabel>
                      </PerfStat>
                      <PerfStat title='In progress. Not yet due'>
                        <PerfValue>{perf.open || 0}</PerfValue>
                        <PerfLabel>open</PerfLabel>
                      </PerfStat>
                    </PerfRow>
                  ) : (
                    <PerfHint>No task history yet</PerfHint>
                  )}
                  <FootBtns>
                    <AddDocBtn onClick={() => setActivityTarget(c)}>
                      <HistoryRoundedIcon style={{ fontSize: 13 }} />
                      Activity
                    </AddDocBtn>
                    <AddDocBtn onClick={() => setPoTarget(c)}>
                      <ReceiptLongRoundedIcon style={{ fontSize: 13 }} />
                      POs
                    </AddDocBtn>
                    <AddDocBtn onClick={() => setDocTarget(c)}>
                      <AddRoundedIcon style={{ fontSize: 13 }} />
                      Link document
                    </AddDocBtn>
                  </FootBtns>
                </CardFoot>
              </Card>
            )
          })}
        </Grid>
        <InfiniteScrollSentinel
          hasMore={!!meta && meta.currentPage < meta.lastPage}
          loading={loadingMore}
          onLoadMore={() => load((meta?.currentPage ?? 1) + 1)}
        />
        </>
      )}

      {editing && (
        <ContractorModal
          draft={editing}
          vettingStatuses={vettingStatuses}
          onClose={() => setEditing(null)}
          onSave={saveContractor}
        />
      )}

      {insightFile && (
        <GuruInsightModal
          open
          onClose={() => setInsightFile(null)}
          file={insightFile}
          bpId={insightFile.buildingPassportId}
        />
      )}

      {folderContractor && (
        <DocumentsFolderModal
          contractor={folderContractor}
          docTypes={docTypes}
          onOpen={doc => openContractorDoc(folderContractor, doc)}
          onRemove={doc => removeDocument(folderContractor, doc)}
          onLink={() => setDocTarget(folderContractor)}
          onClose={() => setFolderTarget(null)}
        />
      )}

      {docTarget && (
        <DocumentModal
          contractor={docTarget}
          docTypes={docTypes}
          onClose={() => setDocTarget(null)}
          onSave={saveDocument}
        />
      )}

      {activityTarget && (
        <ActivityModal
          contractor={activityTarget}
          onClose={() => setActivityTarget(null)}
          onChanged={() => load(1)}
        />
      )}

      {poTarget && (
        <PurchaseOrdersModal
          contractor={poTarget}
          onClose={() => setPoTarget(null)}
        />
      )}

      {showUnassigned && (
        <UnassignedPurchaseOrdersModal
          orders={unassignedPos}
          contractors={contractors}
          onClose={() => setShowUnassigned(false)}
          onChanged={loadUnassigned}
        />
      )}

    </Page>
  )
}

const VETTING_ORDER = ['approved', 'pending_review', 'probationary', 'suspended']

const VettingControl = ({ contractor, statuses = {}, onChange }) => {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)
  const current = VETTING_META[contractor.vettingStatus] || VETTING_META.pending_review
  const backendKeys = Object.keys(statuses)
  const keys = (backendKeys.length ? backendKeys : Object.keys(VETTING_META))
    .slice()
    .sort((a, b) => {
      const ia = VETTING_ORDER.indexOf(a); const ib = VETTING_ORDER.indexOf(b)
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
    })

  useEffect(() => {
    if (!open) return undefined
    const onDocClick = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <VettingWrap ref={wrapRef}>
      <VettingBtn
        type='button'
        $tint={current.tint}
        aria-haspopup='listbox'
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        title={`Vetting status: ${current.label}. ${current.desc} Click to change.`}
      >
        <VettingBtnDot $tint={current.tint} />
        {current.label}
        <VettingCaret $open={open}><ExpandMoreRoundedIcon style={{ fontSize: 14 }} /></VettingCaret>
      </VettingBtn>
      {open && (
        <VettingMenu role='listbox'>
          <VettingMenuHead>
            Vetting status
            <span>How far you’ve checked this supplier. Pick one to change it.</span>
          </VettingMenuHead>
          {keys.map(k => {
            const m = VETTING_META[k] || { label: statuses[k] || k, tint: '#9aa0aa', desc: '' }
            const active = contractor.vettingStatus === k
            return (
              <VettingMenuItem
                key={k}
                type='button'
                role='option'
                aria-selected={active}
                $active={active}
                onClick={() => { onChange(contractor, k); setOpen(false) }}
              >
                <VettingItemDot $tint={m.tint} />
                <VettingItemText>
                  <VettingItemLabel $tint={m.tint}>
                    {m.label}
                    {active && <CurrentTag>Current</CurrentTag>}
                  </VettingItemLabel>
                  {m.desc && <VettingItemDesc>{m.desc}</VettingItemDesc>}
                </VettingItemText>
              </VettingMenuItem>
            )
          })}
        </VettingMenu>
      )}
    </VettingWrap>
  )
}

const StatusExplainer = () => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const onDocClick = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <ExplainerWrap ref={ref}>
      <ExplainerBtn
        type='button'
        aria-label='What do “In use” and “Vetting” mean?'
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        title='What do these two statuses mean?'
      >
        <InfoOutlinedIcon style={{ fontSize: 16 }} />
      </ExplainerBtn>
      {open && (
        <ExplainerPop>
          <ExplainerTitle>Two different things</ExplainerTitle>
          <ExplainerItem>
            <ExplainerHead>In use</ExplainerHead>
            <ExplainerBody>
              Whether you’re working with this supplier right now. Turn it off to park a supplier
              you’re not currently using. They stay on your list.
            </ExplainerBody>
          </ExplainerItem>
          <ExplainerItem>
            <ExplainerHead>Vetting</ExplainerHead>
            <ExplainerBody>
              How far you’ve checked this supplier. From “Pending review” to “Approved”.
              “Suspended” means you’ve barred them from new work after a problem.
            </ExplainerBody>
          </ExplainerItem>
          <ExplainerFoot>
            The two don’t have to agree. A supplier can be in use but not yet approved, or approved
            but not currently in use.
          </ExplainerFoot>
        </ExplainerPop>
      )}
    </ExplainerWrap>
  )
}

const ContractorModal = ({ draft: initial, vettingStatuses = {}, onClose, onSave }) => {
  const [draft, setDraft] = useState({ ...initial, approvedByUserId: initial.approvedBy?.id || null })
  const [pendingFile, setPendingFile] = useState(null)
  const [extracting, setExtracting] = useState(false)
  const [approvers, setApprovers] = useState([])
  const [extractingInsurance, setExtractingInsurance] = useState(false)
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }))

  useEffect(() => {
    if (!draft.id) return
    let live = true
    listContractorApprovers()
      .then(res => { if (live) setApprovers(res?.data?.data || []) })
      .catch(() => {})
    return () => { live = false }
  }, [draft.id])

  const extractInsurance = async () => {
    if (extractingInsurance) return
    setExtractingInsurance(true)
    try {
      const res = await extractContractorInsuranceValue(draft.id)
      const { value, reason } = res?.data?.data || {}
      if (value != null) {
        set('publicLiabilityValue', value)
        toast.success('Read the public liability cover from the certificate. Please check it.')
      } else if (reason === 'no_insurance_document') {
        toast.info('No insurance document on file yet. Add one to the contractor’s documents first.')
      } else if (reason === 'not_analysed') {
        toast.info('That certificate hasn’t been analysed yet. Enter the value manually for now.')
      } else {
        toast.info('Couldn’t find a public liability figure on the certificate. Enter it manually.')
      }
    } catch (err) {
      toast.error('Could not read the certificate. Please enter the value manually.')
    } finally {
      setExtractingInsurance(false)
    }
  }

  const handlePrefill = async file => {
    if (!file) return
    setPendingFile(file)
    setExtracting(true)
    try {
      const startRes = await extractContractorDetails(file)
      const token = startRes?.data?.data?.token
      if (!token) throw new Error('no token')

      const startedAt = Date.now()
      let p = null
      while (Date.now() - startedAt < 120000) {
        await new Promise(resolve => setTimeout(resolve, 2000))
        const statusRes = await getContractorExtractStatus(token)
        const result = statusRes?.data?.data || {}
        if (result.status === 'done') { p = result.data || {}; break }
        if (result.status === 'failed') throw new Error(result.message || 'extraction failed')
      }
      if (!p) throw new Error('timed out')

      setDraft(d => ({
        ...d,
        name: d.name || p.name || '',
        email: d.email || p.email || '',
        company: d.company || p.company || '',
        trade: d.trade || p.trade || '',
        phone: d.phone || p.phone || '',
      }))
      toast.success('Details pre-filled from the document. Please review.')
    } catch (err) {
      const genericErrors = ['no token', 'extraction failed', 'timed out']
      const message = err?.response?.data?.message || (!genericErrors.includes(err?.message) ? err?.message : null)
      toast.error(message || 'Could not read the document. Enter details manually.')
    } finally {
      setExtracting(false)
    }
  }

  return createPortal(
    <Backdrop onClick={onClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHead>
          <ModalIcon><EngineeringRoundedIcon style={{ fontSize: 20 }} /></ModalIcon>
          <ModalHeadText>
            <ModalTitle>{draft.id ? 'Edit contractor' : 'Add contractor'}</ModalTitle>
            <ModalSub>Supply-chain record. Competence, insurance and scope of works.</ModalSub>
          </ModalHeadText>
          <CloseBtn onClick={onClose} aria-label='Close'><CloseRoundedIcon style={{ fontSize: 18 }} /></CloseBtn>
        </ModalHead>

        <ModalBody>
          {!draft.id && (
            <PrefillZone>
              <PrefillBody>
                <PrefillTitle>Start with a document</PrefillTitle>
                <PrefillText>
                  {extracting
                    ? 'Reading the document…'
                    : 'Upload an insurance certificate or letterhead and we’ll pre-fill the details with AI. You can edit everything below.'}
                </PrefillText>
              </PrefillBody>
              <PrefillUpload>
                <input
                  type='file'
                  accept='.pdf,.jpg,.jpeg,.png'
                  style={{ display: 'none' }}
                  disabled={extracting}
                  onChange={e => handlePrefill(e.target.files?.[0])}
                />
                {extracting ? 'Reading…' : (pendingFile ? 'Use another document' : 'Upload document')}
              </PrefillUpload>
            </PrefillZone>
          )}

          <SectionLabel>Contact</SectionLabel>
          <FieldGrid>
            <Field>
              <FieldLabel>Name <Required>*</Required></FieldLabel>
              <Input value={draft.name || ''} onChange={e => set('name', e.target.value)} placeholder='Contact name' autoFocus data-tour-id='contractorModal.name' />
            </Field>
            <Field>
              <FieldLabel>Company</FieldLabel>
              <Input value={draft.company || ''} onChange={e => set('company', e.target.value)} placeholder='Trading name' />
            </Field>
            <Field>
              <FieldLabel>Email <Required>*</Required></FieldLabel>
              <Input type='email' required value={draft.email || ''} onChange={e => set('email', e.target.value)} placeholder='name@company.co.uk' data-tour-id='contractorModal.email' />
            </Field>
            <Field>
              <FieldLabel>Phone</FieldLabel>
              <Input value={draft.phone || ''} onChange={e => set('phone', e.target.value)} placeholder='07…' />
            </Field>
          </FieldGrid>

          <SectionLabel>Trade &amp; scope</SectionLabel>
          <FieldGrid>
            <Field>
              <FieldLabel>Trade</FieldLabel>
              <Input value={draft.trade || ''} onChange={e => set('trade', e.target.value)} placeholder='e.g. Fire systems, Lifts, M&E' />
            </Field>
            <Field $span>
              <FieldLabel>Service scope</FieldLabel>
              <TextArea rows={2} value={draft.serviceScope || ''} onChange={e => set('serviceScope', e.target.value)}
                placeholder='What they are contracted to cover' />
            </Field>
            <Field $span>
              <FieldLabel>Notes</FieldLabel>
              <TextArea rows={2} value={draft.notes || ''} onChange={e => set('notes', e.target.value)}
                placeholder='Anything worth recording about this supplier' />
            </Field>
          </FieldGrid>

          {draft.id && (
            <>
              <SectionLabel>Vetting &amp; contract</SectionLabel>
              <FieldGrid>
                <Field>
                  <FieldLabel>Vetting status</FieldLabel>
                  <SearchableSelect
                    value={draft.vettingStatus || 'pending_review'}
                    onChange={v => set('vettingStatus', v)}
                    options={Object.entries(vettingStatuses).map(([v, l]) => ({ value: v, label: l, color: VETTING_META[v]?.tint }))}
                    ariaLabel='Vetting status'
                  />
                </Field>
                <Field>
                  <FieldLabel>Approved by</FieldLabel>
                  <SearchableSelect
                    value={draft.approvedByUserId}
                    onChange={v => set('approvedByUserId', v || null)}
                    options={approvers.map(u => ({ value: u.id, label: approverName(u) }))}
                    clearable
                    clearLabel='Not recorded'
                    placeholder='Not recorded'
                    searchPlaceholder='Search people…'
                    ariaLabel='Approved by'
                  />
                </Field>
                <Field $span>
                  <FieldLabel>Public liability insurance value (£)</FieldLabel>
                  <Input type='number' min='0' step='1000' value={draft.publicLiabilityValue ?? ''}
                    onChange={e => set('publicLiabilityValue', e.target.value === '' ? null : Number(e.target.value))}
                    placeholder='e.g. 5000000' />
                  <ExtractBtn type='button' disabled={extractingInsurance} onClick={extractInsurance}
                    title='Read the public liability limit from the contractor’s uploaded insurance certificate'>
                    <AutoAwesomeRoundedIcon style={{ fontSize: 13 }} />
                    {extractingInsurance ? 'Reading certificate…' : 'Extract from certificate'}
                  </ExtractBtn>
                </Field>
                <Field>
                  <FieldLabel>Contract start date</FieldLabel>
                  <Input type='date' value={draft.contractStartDate || ''}
                    onChange={e => set('contractStartDate', e.target.value || null)} />
                </Field>
                <Field>
                  <FieldLabel>Contract term (months)</FieldLabel>
                  <Input type='number' min='1' max='600' value={draft.contractTermMonths ?? ''}
                    onChange={e => set('contractTermMonths', e.target.value === '' ? null : Number(e.target.value))}
                    placeholder='e.g. 12' />
                </Field>
                <Field>
                  <FieldLabel>Next review date</FieldLabel>
                  <Input type='date' value={draft.nextReviewDate || ''}
                    onChange={e => set('nextReviewDate', e.target.value || null)} />
                </Field>
                <Field>
                  <FieldLabel>Review cadence (months)</FieldLabel>
                  <Input type='number' min='1' max='120' value={draft.reviewCadenceMonths ?? ''}
                    onChange={e => set('reviewCadenceMonths', e.target.value === '' ? null : Number(e.target.value))}
                    placeholder='e.g. 12' />
                </Field>
              </FieldGrid>
              <VettingHint>
                Contract start and next review dates show automatically in your calendar feed. “Approved by” lists the people in your organisation.
              </VettingHint>
            </>
          )}

          {draft.id && (
            <ActiveRow>
              <input
                type='checkbox'
                id='contractor-active'
                checked={!!draft.isActive}
                onChange={e => set('isActive', e.target.checked)} />
              <label htmlFor='contractor-active'>Active supplier</label>
            </ActiveRow>
          )}
        </ModalBody>

        <ModalFoot>
          <GhostBtn onClick={onClose}>Cancel</GhostBtn>
          <PrimaryBtn
            data-tour-id='contractorModal.save'
            disabled={!draft.name?.trim() || !draft.email?.trim() || extracting}
            onClick={() => onSave({
              id: draft.id,
              name: draft.name,
              company: draft.company || null,
              email: draft.email,
              phone: draft.phone || null,
              trade: draft.trade || null,
              service_scope: draft.serviceScope || null,
              notes: draft.notes || null,
              ...(draft.id ? {
                is_active: !!draft.isActive,
                vetting_status: draft.vettingStatus || 'pending_review',
                public_liability_value: draft.publicLiabilityValue ?? null,
                contract_start_date: draft.contractStartDate || null,
                contract_term_months: draft.contractTermMonths ?? null,
                next_review_date: draft.nextReviewDate || null,
                review_cadence_months: draft.reviewCadenceMonths ?? null,
                approved_by_user_id: draft.approvedByUserId || null,
              } : {}),
            }, pendingFile)}>
            {draft.id ? 'Save changes' : 'Add contractor'}
          </PrimaryBtn>
        </ModalFoot>
      </ModalCard>
    </Backdrop>,
    document.body
  )
}

const DocumentsFolderModal = ({ contractor, docTypes, onOpen, onRemove, onLink, onClose }) => {
  const docs = contractor.documents || []
  const groups = useMemo(() => {
    const order = Object.keys(docTypes)
    const byType = {}
    docs.forEach(d => { (byType[d.docType] = byType[d.docType] || []).push(d) })
    return Object.keys(byType)
      .sort((a, b) => {
        const ia = order.indexOf(a); const ib = order.indexOf(b)
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
      })
      .map(k => ({ key: k, label: docTypes[k] || k, items: byType[k] }))
  }, [docs, docTypes])

  return createPortal(
    <Backdrop onClick={onClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHead>
          <ModalIcon><FolderRoundedIcon style={{ fontSize: 20 }} /></ModalIcon>
          <ModalHeadText>
            <ModalTitle>Documents for {contractor.name}</ModalTitle>
            <ModalSub>Every document kept for this contractor. Each one lives on a Building Passport and is tracked here for expiry.</ModalSub>
          </ModalHeadText>
          <CloseBtn onClick={onClose} aria-label='Close'><CloseRoundedIcon style={{ fontSize: 18 }} /></CloseBtn>
        </ModalHead>

        <ModalBody>
          {docs.length === 0 ? (
            <FolderEmpty>
              <FolderRoundedIcon style={{ fontSize: 32, color: '#c4c9d4' }} />
              <FolderEmptyTitle>No documents yet</FolderEmptyTitle>
              <FolderEmptyText>
                Link a document from one of your Building Passports. It stays on the building
                and is tracked here for expiry. Documents this contractor is detected in are
                added automatically.
              </FolderEmptyText>
            </FolderEmpty>
          ) : (
            groups.map(g => (
              <FolderGroup key={g.key}>
                <FolderGroupHead>
                  <FolderGroupDot $tint={DOC_TINT[g.key] || '#9aa0aa'} />
                  {g.label}
                  <FolderGroupCount>{g.items.length}</FolderGroupCount>
                </FolderGroupHead>
                {g.items.map(d => (
                  <DocRow key={d.id} $expired={d.isExpired}>
                    <DocBadges>
                      <DocType $tint={DOC_TINT[d.docType] || '#9aa0aa'}>
                        <DescriptionRoundedIcon style={{ fontSize: 12 }} />
                        {docTypes[d.docType] || d.docType}
                      </DocType>
                      {d.source === 'auto' && (
                        <AutoDocTag title='Automatically linked from the document this contractor was detected in'>
                          <AutoAwesomeRoundedIcon style={{ fontSize: 10 }} />
                          Auto
                        </AutoDocTag>
                      )}
                    </DocBadges>
                    <DocName title={d.policyNumber ? `Policy ${d.policyNumber}${d.coverAmount ? ` · £${Number(d.coverAmount).toLocaleString('en-GB')}` : ''}` : undefined}>
                      {d.displayName}
                      {d.coverAmount != null && (
                        <DocCover>£{Number(d.coverAmount).toLocaleString('en-GB')}</DocCover>
                      )}
                    </DocName>
                    <DocExpiry $expired={d.isExpired} $soon={d.expiresSoon}>
                      {d.expiresAt
                        ? d.isExpired ? `expired ${d.expiresAt}` : d.expiresAt
                        : 'no expiry'}
                    </DocExpiry>
                    {(d.fileId || d.hasFile) ? (
                      <DocDelete onClick={() => onOpen(d)}
                        title={d.fileId ? 'Open the document insight' : 'Download file'}>
                        {d.fileId
                          ? <OpenInNewRoundedIcon style={{ fontSize: 13 }} />
                          : <DownloadRoundedIcon style={{ fontSize: 13 }} />}
                      </DocDelete>
                    ) : <span />}
                    <DocDelete onClick={() => onRemove(d)} title='Remove'>
                      <DeleteRoundedIcon style={{ fontSize: 13 }} />
                    </DocDelete>
                  </DocRow>
                ))}
              </FolderGroup>
            ))
          )}
        </ModalBody>

        <ModalFoot>
          <GhostBtn onClick={onClose}>Close</GhostBtn>
          <PrimaryBtn onClick={onLink}>
            <AddRoundedIcon style={{ fontSize: 15 }} />
            Link document
          </PrimaryBtn>
        </ModalFoot>
      </ModalCard>
    </Backdrop>,
    document.body
  )
}

const DocumentModal = ({ contractor, docTypes, onClose, onSave }) => {
  const [draft, setDraft] = useState({
    docType: 'insurance', displayName: '', validFrom: '', expiresAt: '',
    policyNumber: '', coverAmount: '',
  })
  const [saving, setSaving] = useState(false)
  const [nameTouched, setNameTouched] = useState(false)

  const [bps, setBps] = useState([])
  const [bpsLoading, setBpsLoading] = useState(true)
  const [bpSearch, setBpSearch] = useState('')
  const [selectedBp, setSelectedBp] = useState(null)
  const [files, setFiles] = useState([])
  const [filesLoading, setFilesLoading] = useState(false)
  const [fileSearch, setFileSearch] = useState('')
  const [selectedFile, setSelectedFile] = useState(null)

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }))

  useEffect(() => {
    let alive = true
    setBpsLoading(true)
    fetchUserBps({ page: 1, sort: 'name', filters: '&limit=250' })
      .then(res => { if (alive) setBps(res?.data?.entries || res?.data?.data || []) })
      .catch(() => { if (alive) toast.error('Could not load your Building Passports.') })
      .finally(() => { if (alive) setBpsLoading(false) })
    return () => { alive = false }
  }, [])

  const pickBp = bp => {
    setSelectedBp(bp)
    setSelectedFile(null)
    setFiles([])
    setFileSearch('')
    setFilesLoading(true)
    getFilesListForBp({ id: bp.id, query: { filter: { include_identity_documents: 1 } } })
      .then(res => setFiles(res?.data?.data || res?.data || []))
      .catch(() => toast.error('Could not load this Building Passport’s documents.'))
      .finally(() => setFilesLoading(false))
  }

  const pickFile = f => {
    setSelectedFile(f)
    if (!nameTouched) set('displayName', f.displayName || f.name || 'Document')
  }

  const filteredBps = useMemo(() => {
    const q = bpSearch.trim().toLowerCase()
    if (!q) return bps
    return bps.filter(b => [b.name, b.uprn, b.street, b.city, b.postalCode]
      .some(v => (v || '').toString().toLowerCase().includes(q)))
  }, [bps, bpSearch])

  const filteredFiles = useMemo(() => {
    const q = fileSearch.trim().toLowerCase()
    if (!q) return files
    return files.filter(f => `${f.displayName || f.name || ''} ${f.fileType?.name || ''}`.toLowerCase().includes(q))
  }, [files, fileSearch])

  const canSave = !!draft.displayName.trim() && !!selectedFile && !saving

  const submit = async () => {
    if (!canSave) return
    setSaving(true)
    try {
      await onSave({
        doc_type: draft.docType,
        display_name: draft.displayName,
        valid_from: draft.validFrom || null,
        expires_at: draft.expiresAt || null,
        policy_number: draft.policyNumber || null,
        cover_amount: draft.coverAmount !== '' ? Number(draft.coverAmount) : null,
        file_id: selectedFile.id,
      })
    } catch (e) {
      toast.error('Could not link that document.')
    } finally {
      setSaving(false)
    }
  }

  return createPortal(
    <Backdrop onClick={onClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHead>
          <ModalIcon><DescriptionRoundedIcon style={{ fontSize: 20 }} /></ModalIcon>
          <ModalHeadText>
            <ModalTitle>Link a document</ModalTitle>
            <ModalSub>{contractor.name}. Pick a document already stored on one of your Building Passports. It stays on the building and is tracked here for expiry.</ModalSub>
          </ModalHeadText>
          <CloseBtn onClick={onClose} aria-label='Close'><CloseRoundedIcon style={{ fontSize: 18 }} /></CloseBtn>
        </ModalHead>

        <ModalBody>
          <FieldGrid>
            <Field $span>
              <FieldLabel>
                {selectedBp
                  ? <>Document from <strong>{selectedBp.name}</strong> <Required>*</Required></>
                  : <>Choose a Building Passport <Required>*</Required></>}
              </FieldLabel>
              {selectedBp && (
                <PickerBack type='button' onClick={() => { setSelectedBp(null); setSelectedFile(null) }}>
                  ← Choose a different Building Passport
                </PickerBack>
              )}
              <PickerSearch>
                <SearchRoundedIcon style={{ fontSize: 16, color: '#94a3b8' }} />
                <PickerSearchInput
                  placeholder={selectedBp ? 'Search documents…' : 'Search your Building Passports…'}
                  value={selectedBp ? fileSearch : bpSearch}
                  onChange={e => (selectedBp ? setFileSearch(e.target.value) : setBpSearch(e.target.value))}
                />
              </PickerSearch>
              <PickerList>
                {!selectedBp && bpsLoading && [0, 1, 2].map(i => <PickerSkeleton key={i} />)}
                {!selectedBp && !bpsLoading && filteredBps.length === 0 && (
                  <PickerEmpty>No Building Passports found.</PickerEmpty>
                )}
                {!selectedBp && !bpsLoading && filteredBps.map(b => (
                  <PickerRow key={b.id} type='button' onClick={() => pickBp(b)}>
                    <PickerRowText>
                      <PickerRowName>{b.name}</PickerRowName>
                      {(b.street || b.city || b.postalCode) && (
                        <PickerRowMeta>{[b.street, b.city, b.postalCode].filter(Boolean).join(', ')}</PickerRowMeta>
                      )}
                    </PickerRowText>
                    <OpenInNewRoundedIcon style={{ fontSize: 14, color: '#c4c9d4' }} />
                  </PickerRow>
                ))}
                {selectedBp && filesLoading && [0, 1, 2].map(i => <PickerSkeleton key={i} />)}
                {selectedBp && !filesLoading && filteredFiles.length === 0 && (
                  <PickerEmpty>No documents on this Building Passport yet. Upload it to the building first, then link it here.</PickerEmpty>
                )}
                {selectedBp && !filesLoading && filteredFiles.map(f => (
                  <PickerRow key={f.id} type='button' $active={selectedFile?.id === f.id} onClick={() => pickFile(f)}>
                    <DescriptionRoundedIcon style={{ fontSize: 16, color: '#94a3b8' }} />
                    <PickerRowText>
                      <PickerRowName>{f.displayName || f.name || 'Document'}</PickerRowName>
                      {f.fileType?.name && <PickerRowMeta>{f.fileType.name}</PickerRowMeta>}
                    </PickerRowText>
                    <PickerRadio $active={selectedFile?.id === f.id} />
                  </PickerRow>
                ))}
              </PickerList>
            </Field>

            <Field>
              <FieldLabel>Type</FieldLabel>
              <SearchableSelect
                value={draft.docType}
                onChange={v => set('docType', v)}
                options={Object.entries(docTypes).map(([v, l]) => ({ value: v, label: l }))}
                ariaLabel='Document type'
              />
            </Field>
            <Field>
              <FieldLabel>Name <Required>*</Required></FieldLabel>
              <Input value={draft.displayName}
                onChange={e => { setNameTouched(true); set('displayName', e.target.value) }}
                placeholder='e.g. Public liability 2026' />
            </Field>
            {draft.docType === 'insurance' && (
              <>
                <Field>
                  <FieldLabel>Policy number</FieldLabel>
                  <Input value={draft.policyNumber} onChange={e => set('policyNumber', e.target.value)} placeholder='Policy ref' />
                </Field>
                <Field>
                  <FieldLabel>Cover amount (£)</FieldLabel>
                  <Input type='number' min='0' value={draft.coverAmount}
                    onChange={e => set('coverAmount', e.target.value)}
                    placeholder='e.g. 10000000' />
                </Field>
              </>
            )}
            <Field>
              <FieldLabel>Valid from</FieldLabel>
              <Input type='date' value={draft.validFrom} onChange={e => set('validFrom', e.target.value)} />
            </Field>
            <Field>
              <FieldLabel>Expires</FieldLabel>
              <Input type='date' value={draft.expiresAt} onChange={e => set('expiresAt', e.target.value)} />
            </Field>
          </FieldGrid>
        </ModalBody>

        <ModalFoot>
          <GhostBtn onClick={onClose} disabled={saving}>Cancel</GhostBtn>
          <PrimaryBtn disabled={!canSave} onClick={submit}>
            {saving ? 'Linking…' : 'Link document'}
          </PrimaryBtn>
        </ModalFoot>
      </ModalCard>
    </Backdrop>,
    document.body
  )
}

const ACTIVITY_LABELS = {
  created: 'Created',
  updated: 'Updated',
  document_added: 'Document added',
  document_removed: 'Document removed',
  vetting_changed: 'Vetting changed',
  assigned_to_task: 'Assigned to task',
  task_assigned: 'Assigned a task',
  task_form_submitted: 'Submitted a form',
  assigned_to_incident: 'Linked to incident',
  task_completed: 'Completed a task',
  merged: 'Merged in contractors',
  merge_reversed: 'Merge reversed',
  deleted: 'Deleted',
  approved: 'Approved',
}

const ACTIVITY_TINT = {
  created: '#2aa6b3',
  updated: '#9aa0aa',
  document_added: '#33becc',
  document_removed: '#9aa0aa',
  vetting_changed: '#d97706',
  assigned_to_task: '#33becc',
  task_assigned: '#33becc',
  task_form_submitted: '#7C5FE0',
  assigned_to_incident: '#9333ea',
  task_completed: '#15803d',
  merged: '#7C5FE0',
  merge_reversed: '#d97706',
  deleted: '#b91c1c',
  approved: '#15803d',
}

const ActivityModal = ({ contractor, onClose, onChanged }) => {
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [reloadKey, setReloadKey] = useState(0)
  const [undoingId, setUndoingId] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listContractorActivity(contractor.id)
      .then(res => {
        if (cancelled) return
        setRows(res?.data?.data?.activity || [])
        setTotal(res?.data?.data?.total || 0)
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [contractor.id, reloadKey])

  const handleUndo = async (mergeId) => {
    if (undoingId) return
    setUndoingId(mergeId)
    try {
      await undoContractorMerge(mergeId)
      toast.success('Merge reversed. The contractors have been restored.')
      setReloadKey(k => k + 1)
      onChanged?.()
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not reverse this merge.')
    } finally {
      setUndoingId(null)
    }
  }

  return createPortal(
    <Backdrop onClick={onClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHead>
          <ModalIcon><HistoryRoundedIcon style={{ fontSize: 20 }} /></ModalIcon>
          <ModalHeadText>
            <ModalTitle>Activity</ModalTitle>
            <ModalSub>{contractor.name}{contractor.company ? `: ${contractor.company}` : ''}</ModalSub>
          </ModalHeadText>
          <CloseBtn onClick={onClose} aria-label='Close'><CloseRoundedIcon style={{ fontSize: 18 }} /></CloseBtn>
        </ModalHead>
        <ModalBody>
          {loading ? (
            <ActivityFeed>
              {[0, 1, 2, 3].map(i => (
                <ActivityRow key={i}>
                  <ActivityDot style={{ background: '#e5e7eb' }} />
                  <ActivityMain>
                    <SkeletonLine style={{ width: '72%' }} />
                    <SkeletonLine style={{ width: '40%', height: 9 }} />
                  </ActivityMain>
                </ActivityRow>
              ))}
            </ActivityFeed>
          ) : rows.length === 0 ? (
            <ActivityEmpty>No activity recorded yet.</ActivityEmpty>
          ) : (
            <ActivityFeed>
              {rows.map(r => (
                <ActivityRow key={r.id}>
                  <ActivityDot $tint={ACTIVITY_TINT[r.action]} />
                  <ActivityMain>
                    <ActivityText>
                      <strong>{ACTIVITY_LABELS[r.action] || r.action}</strong>
                      {r.description ? `: ${r.description}` : ''}
                    </ActivityText>
                    <ActivityMeta>
                      {r.actor ? `${r.actor} · ` : ''}{r.createdAt ? new Date(r.createdAt).toLocaleString('en-GB') : ''}
                    </ActivityMeta>
                    {r.action === 'merged' && r.reversible && r.contractorMergeId && (
                      <UndoMergeBtn
                        type='button'
                        disabled={undoingId === r.contractorMergeId}
                        onClick={() => handleUndo(r.contractorMergeId)}
                      >
                        {undoingId === r.contractorMergeId ? 'Reversing…' : 'Undo merge'}
                      </UndoMergeBtn>
                    )}
                  </ActivityMain>
                </ActivityRow>
              ))}
              {total > rows.length && (
                <ActivityEmpty>Showing the latest {rows.length} of {total} events.</ActivityEmpty>
              )}
            </ActivityFeed>
          )}
        </ModalBody>
      </ModalCard>
    </Backdrop>,
    document.body
  )
}

const PO_STATUS_META = {
  pending:  { label: 'Pending',  fg: '#d97706', bg: '#fef3c7' },
  approved: { label: 'Approved', fg: '#15803d', bg: '#dcfce7' },
  rejected: { label: 'Rejected', fg: '#b00020', bg: '#fdecea' },
}

const fmtMoney = (v, ccy = 'GBP') => {
  if (v === null || v === undefined || v === '') return 'Not set'
  const n = Number(v)
  if (Number.isNaN(n)) return 'Not set'
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: ccy || 'GBP', maximumFractionDigits: 2 }).format(n)
  } catch {
    return `£${n.toFixed(2)}`
  }
}

const BLANK_PO_FORM = { poNumber: '', value: '', contractId: '', description: '', status: 'pending' }

const PurchaseOrdersModal = ({ contractor, onClose }) => {
  const [orders, setOrders] = useState([])
  const [contracts, setContracts] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(BLANK_PO_FORM)
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const load = useCallback(() => {
    setLoading(true)
    listPurchaseOrders(contractor.id)
      .then(res => setOrders(res?.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [contractor.id])

  useEffect(() => {
    load()
    listContracts({ contractor_id: contractor.id })
      .then(res => setContracts(res?.data?.data || []))
      .catch(() => {})
  }, [contractor.id, load])

  const editingPo = editingId && editingId !== 'new' ? orders.find(o => o.id === editingId) : null
  const isTaskEdit = editingPo?.source === 'task'

  const startAdd = () => { setForm(BLANK_PO_FORM); setEditingId('new') }
  const startEdit = po => {
    setForm({
      poNumber: po.poNumber || '',
      value: po.value ?? '',
      contractId: po.contractId || '',
      description: po.description || '',
      status: po.status || 'pending',
    })
    setEditingId(po.id)
  }
  const cancelEdit = () => { setEditingId(null); setForm(BLANK_PO_FORM) }

  const submit = async () => {
    if (!form.poNumber.trim()) { toast.error('Enter a PO number.'); return }
    const payload = {
      poNumber: form.poNumber.trim(),
      value: form.value === '' ? null : Number(form.value),
      description: form.description.trim() || null,
      contractId: form.contractId || null,
      status: form.status,
    }
    setSaving(true)
    try {
      if (editingId === 'new') {
        await createPurchaseOrder(contractor.id, payload)
        toast.success('Purchase order raised.')
      } else {
        if (isTaskEdit) delete payload.poNumber
        await updatePurchaseOrder(editingId, payload)
        toast.success('Purchase order updated.')
      }
      cancelEdit()
      load()
    } catch {
      toast.error('Could not save the purchase order.')
    } finally {
      setSaving(false)
    }
  }

  const changeStatus = async (po, status) => {
    if (po.status === status) return
    setOrders(list => list.map(o => o.id === po.id ? { ...o, status } : o))
    try {
      await updatePurchaseOrder(po.id, { status })
      load()
    } catch {
      toast.error('Could not update the status.')
      load()
    }
  }

  const remove = async po => {
    if (!window.confirm('Delete this purchase order?')) return
    try {
      await deletePurchaseOrder(po.id)
      toast.success('Purchase order deleted.')
      load()
    } catch {
      toast.error('Could not delete the purchase order.')
    }
  }

  const total = orders.reduce((s, p) => s + (Number(p.value) || 0), 0)
  const pendingCount = orders.filter(p => p.status === 'pending').length

  const renderForm = () => (
    <PoForm>
      <PoFormGrid>
        <PoField style={{ gridColumn: '1 / -1' }}>
          <PoLabel>PO number</PoLabel>
          <PoInput
            value={form.poNumber}
            disabled={isTaskEdit}
            onChange={e => setF('poNumber', e.target.value)}
            placeholder='e.g. PO-2026-001234'
          />
          {isTaskEdit && <PoHint>Raised on a task. Change the number on the task itself.</PoHint>}
        </PoField>
        <PoField>
          <PoLabel>Value (£)</PoLabel>
          <PoInput type='number' min='0' step='0.01' value={form.value} onChange={e => setF('value', e.target.value)} placeholder='0.00' />
        </PoField>
        <PoField>
          <PoLabel>Approval status</PoLabel>
          <SearchableSelect
            value={form.status}
            onChange={v => setF('status', v)}
            options={Object.entries(PO_STATUS_META).map(([v, m]) => ({ value: v, label: m.label, color: m.fg }))}
            ariaLabel='Approval status'
          />
        </PoField>
        {contracts.length > 0 && (
          <PoField style={{ gridColumn: '1 / -1' }}>
            <PoLabel>Related contract (optional)</PoLabel>
            <SearchableSelect
              value={form.contractId}
              onChange={v => setF('contractId', v || '')}
              options={contracts.map(c => ({
                value: c.id,
                label: `${c.reference || c.title || 'Contract'}${c.title && c.reference ? `: ${c.title}` : ''}`,
              }))}
              clearable
              clearLabel='Not linked to a contract'
              placeholder='Not linked to a contract'
              searchPlaceholder='Search contracts…'
              ariaLabel='Related contract'
            />
          </PoField>
        )}
        <PoField style={{ gridColumn: '1 / -1' }}>
          <PoLabel>Description (optional)</PoLabel>
          <PoInput value={form.description} onChange={e => setF('description', e.target.value)} placeholder='What is this purchase order for?' />
        </PoField>
      </PoFormGrid>
      <PoFormActions>
        <PoGhostBtn onClick={cancelEdit} disabled={saving}>Cancel</PoGhostBtn>
        <PoPrimaryBtn onClick={submit} disabled={saving}>
          {saving ? 'Saving…' : (editingId === 'new' ? 'Raise PO' : 'Save changes')}
        </PoPrimaryBtn>
      </PoFormActions>
    </PoForm>
  )

  return createPortal(
    <Backdrop onClick={onClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHead>
          <ModalIcon><ReceiptLongRoundedIcon style={{ fontSize: 20 }} /></ModalIcon>
          <ModalHeadText>
            <ModalTitle>Purchase orders</ModalTitle>
            <ModalSub>{contractor.name}{contractor.company ? `: ${contractor.company}` : ''}</ModalSub>
          </ModalHeadText>
          <CloseBtn onClick={onClose} aria-label='Close'><CloseRoundedIcon style={{ fontSize: 18 }} /></CloseBtn>
        </ModalHead>
        <ModalBody>
          <PoSummary>
            <span><strong>{orders.length}</strong> purchase order{orders.length === 1 ? '' : 's'}</span>
            <PoDot />
            <span><strong>{fmtMoney(total)}</strong> total</span>
            {pendingCount > 0 && (<><PoDot /><span><strong>{pendingCount}</strong> pending approval</span></>)}
          </PoSummary>

          {editingId === 'new' ? renderForm() : (
            <AddPoBtn onClick={startAdd}><AddRoundedIcon style={{ fontSize: 15 }} /> Add purchase order</AddPoBtn>
          )}

          {loading ? (
            <PoList>
              {[0, 1, 2].map(i => (
                <PoRow key={i} as='div'>
                  <PoMain>
                    <SkeletonLine style={{ width: '46%' }} />
                    <SkeletonLine style={{ width: '64%', height: 9 }} />
                  </PoMain>
                </PoRow>
              ))}
            </PoList>
          ) : orders.length === 0 ? (
            <ActivityEmpty>No purchase orders yet. Add one above, or raise one on a task and it appears here.</ActivityEmpty>
          ) : (
            <PoList>
              {orders.map(po => editingId === po.id ? (
                <div key={po.id}>{renderForm()}</div>
              ) : (
                <PoRow key={po.id}>
                  <PoMain>
                    <PoNumberLine>
                      <PoNumber>{po.poNumber}</PoNumber>
                      {po.source === 'task' && <SrcBadge><ReceiptLongRoundedIcon style={{ fontSize: 11 }} /> From task</SrcBadge>}
                    </PoNumberLine>
                    {(po.task?.title || po.contract?.reference) && (
                      <PoMeta>
                        {po.task?.title ? po.task.title : ''}
                        {po.task?.title && po.contract?.reference ? ' · ' : ''}
                        {po.contract?.reference ? `Contract ${po.contract.reference}` : ''}
                      </PoMeta>
                    )}
                    {po.description && <PoDesc>{po.description}</PoDesc>}
                    {po.status === 'approved' && po.approvedByName && (
                      <PoApproved>Approved by {po.approvedByName}{po.approvedAt ? ` · ${new Date(po.approvedAt).toLocaleDateString('en-GB')}` : ''}</PoApproved>
                    )}
                  </PoMain>
                  <PoSide>
                    <PoValue>{fmtMoney(po.value, po.currency)}</PoValue>
                    <div style={{ width: 140 }}>
                      <SearchableSelect
                        value={po.status}
                        onChange={v => changeStatus(po, v)}
                        options={Object.entries(PO_STATUS_META).map(([v, m]) => ({ value: v, label: m.label, color: m.fg }))}
                        ariaLabel='Approval status'
                      />
                    </div>
                    <PoActions>
                      <PoIconBtn onClick={() => startEdit(po)} title='Edit'><EditRoundedIcon style={{ fontSize: 14 }} /></PoIconBtn>
                      {po.source !== 'task' && (
                        <PoIconBtn onClick={() => remove(po)} title='Delete' $danger><DeleteRoundedIcon style={{ fontSize: 14 }} /></PoIconBtn>
                      )}
                    </PoActions>
                  </PoSide>
                </PoRow>
              ))}
            </PoList>
          )}
        </ModalBody>
      </ModalCard>
    </Backdrop>,
    document.body
  )
}

const UnassignedPurchaseOrdersModal = ({ orders, contractors, onClose, onChanged }) => {
  const [assigningId, setAssigningId] = useState(null)

  const assign = async (po, contractorId) => {
    if (!contractorId) return
    setAssigningId(po.id)
    try {
      await updatePurchaseOrder(po.id, { contractorId })
      const c = contractors.find(x => String(x.id) === String(contractorId))
      toast.success(`${po.poNumber} filed under ${c?.name || 'the contractor'}.`)
      onChanged()
    } catch {
      toast.error('Could not assign the purchase order.')
    } finally {
      setAssigningId(null)
    }
  }

  return createPortal(
    <Backdrop onClick={onClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHead>
          <ModalIcon><ReceiptLongRoundedIcon style={{ fontSize: 20 }} /></ModalIcon>
          <ModalHeadText>
            <ModalTitle>Unassigned purchase orders</ModalTitle>
            <ModalSub>Raised on tasks that have no contractor. Assign each one to file it under that contractor’s POs.</ModalSub>
          </ModalHeadText>
          <CloseBtn onClick={onClose} aria-label='Close'><CloseRoundedIcon style={{ fontSize: 18 }} /></CloseBtn>
        </ModalHead>
        <ModalBody>
          {orders.length === 0 ? (
            <ActivityEmpty>Nothing unassigned. Every purchase order is filed under a contractor.</ActivityEmpty>
          ) : (
            <PoList>
              {orders.map(po => (
                <PoRow key={po.id}>
                  <PoMain>
                    <PoNumberLine>
                      <PoNumber>{po.poNumber}</PoNumber>
                      <SrcBadge><ReceiptLongRoundedIcon style={{ fontSize: 11 }} /> From task</SrcBadge>
                      <PoStatusChip $status={po.status}>{PO_STATUS_META[po.status]?.label || po.status}</PoStatusChip>
                    </PoNumberLine>
                    {(po.task?.title || po.task?.bp?.name) && (
                      <PoMeta>
                        {po.task?.title || ''}{po.task?.title && po.task?.bp?.name ? ' · ' : ''}{po.task?.bp?.name || ''}
                      </PoMeta>
                    )}
                  </PoMain>
                  <PoSide>
                    {contractors.length === 0 ? (
                      <PoHint>Add a contractor first</PoHint>
                    ) : (
                      <div style={{ width: 220, maxWidth: '100%' }}>
                        <SearchableSelect
                          value={null}
                          onChange={v => assign(po, v)}
                          options={contractors.map(c => ({ value: c.id, label: c.name, sublabel: c.company || undefined }))}
                          placeholder={assigningId === po.id ? 'Assigning…' : 'Assign to contractor…'}
                          searchPlaceholder='Search contractors…'
                          disabled={assigningId === po.id}
                          ariaLabel='Assign to contractor'
                        />
                      </div>
                    )}
                  </PoSide>
                </PoRow>
              ))}
            </PoList>
          )}
        </ModalBody>
      </ModalCard>
    </Backdrop>,
    document.body
  )
}

export default ContractorsPage

const PoSummary = styled.div`
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  font-size: 12.5px; color: #475467; margin-bottom: 12px;
  strong { color: #1d2433; font-weight: 700; }
`
const PoDot = styled.span`
  width: 3px; height: 3px; border-radius: 50%; background: #c4c9d2;
`
const AddPoBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  border: 1px dashed #cdd3dc; background: #fafbfc; color: #2aa6b3;
  font-size: 12.5px; font-weight: 700; padding: 9px 14px; border-radius: 10px;
  cursor: pointer; width: 100%; justify-content: center; margin-bottom: 14px;
  &:hover { background: #f2fafb; border-color: #9fd4da; }
`
const PoForm = styled.div`
  border: 1px solid #e5e7eb; border-radius: 12px; padding: 14px; margin-bottom: 14px;
  background: #fcfcfd;
`
const PoFormGrid = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px 12px;

  @media (max-width: 480px) { grid-template-columns: minmax(0, 1fr); }
`
const PoField = styled.div` display: flex; flex-direction: column; gap: 5px; min-width: 0; `
const PoLabel = styled.label`
  font-size: 10.5px; font-weight: 700; letter-spacing: 0.04em;
  text-transform: uppercase; color: #8a909b;
`
const PoInput = styled.input`
  border: 1px solid #dfe3e8; border-radius: 8px; padding: 8px 10px;
  font-size: 13px; color: #1d2433; background: #fff; width: 100%;
  &:focus { outline: none; border-color: #2aa6b3; }
  &:disabled { background: #f3f4f6; color: #8a909b; }
`
const PoHint = styled.span` font-size: 11px; color: #8a909b; `
const PoFormActions = styled.div`
  display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px;
`
const PoGhostBtn = styled.button`
  border: 1px solid #dfe3e8; background: #fff; color: #475467;
  font-size: 12.5px; font-weight: 700; padding: 8px 14px; border-radius: 9px; cursor: pointer;
  &:hover { background: #f7f8fa; }
  &:disabled { opacity: 0.5; cursor: default; }
`
const PoPrimaryBtn = styled.button`
  border: 1px solid #101828; background: #101828; color: #fff;
  font-size: 12.5px; font-weight: 700; padding: 8px 16px; border-radius: 9px; cursor: pointer;
  &:hover { background: #1d2433; }
  &:disabled { opacity: 0.55; cursor: default; }
`
const PoList = styled.div` display: flex; flex-direction: column; gap: 2px; `
const PoRow = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 14px; align-items: start;
  padding: 12px 2px; border-bottom: 1px solid #f2f2f4;
  &:last-child { border-bottom: none; }
`
const PoMain = styled.div` display: flex; flex-direction: column; gap: 4px; min-width: 0; `
const PoNumberLine = styled.div` display: flex; align-items: center; gap: 8px; flex-wrap: wrap; `
const PoNumber = styled.span` font-size: 13.5px; font-weight: 700; color: #1d2433; `
const SrcBadge = styled.span`
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 10.5px; font-weight: 700; color: #475467; background: #eef0f3;
  padding: 2px 7px; border-radius: 999px;
`
const PoMeta = styled.span` font-size: 12px; color: #667085; `
const PoDesc = styled.span` font-size: 12px; color: #475467; `
const PoApproved = styled.span` font-size: 11.5px; color: #15803d; font-weight: 600; `
const PoSide = styled.div`
  display: flex; flex-direction: column; align-items: flex-end; gap: 7px;
`
const PoValue = styled.span` font-size: 13.5px; font-weight: 700; color: #1d2433; white-space: nowrap; `
const PoStatusChip = styled.span`
  font-size: 10.5px; font-weight: 700; padding: 2px 8px; border-radius: 999px;
  color: ${({ $status }) => PO_STATUS_META[$status]?.fg || '#475467'};
  background: ${({ $status }) => PO_STATUS_META[$status]?.bg || '#f3f4f6'};
`
const UnassignedBanner = styled.button`
  font: inherit; display: flex; align-items: center; gap: 12px; width: 100%;
  text-align: left; cursor: pointer; margin-bottom: 18px;
  border: 1px solid #fcd9b6; background: #fff7ed; border-radius: 12px; padding: 12px 14px;
  &:hover { background: #fff1e0; }
`
const UnassignedIcon = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
  background: #ffedd5; color: #c2410c;
`
const UnassignedText = styled.span`
  font-size: 13px; color: #7c2d12; flex: 1; min-width: 0;
  strong { font-weight: 800; }
`
const UnassignedBtn = styled.span`
  font-size: 12px; font-weight: 800; color: #c2410c; white-space: nowrap;
`
const PoActions = styled.div` display: flex; gap: 4px; `
const PoIconBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  width: 26px; height: 26px; border-radius: 7px; cursor: pointer;
  border: 1px solid #e5e7eb; background: #fff;
  color: ${({ $danger }) => ($danger ? '#b00020' : '#667085')};
  &:hover { background: ${({ $danger }) => ($danger ? '#fdecea' : '#f5f6f8')}; }
`

const ActivityFeed = styled.div`
  display: flex; flex-direction: column; gap: 2px;
`
const ActivityRow = styled.div`
  display: grid; grid-template-columns: 14px minmax(0, 1fr); gap: 12px;
  padding: 10px 2px;
  border-bottom: 1px solid #f2f2f4;
  &:last-child { border-bottom: none; }
`
const ActivityDot = styled.span`
  width: 8px; height: 8px; margin-top: 6px;
  border-radius: 999px;
  background: ${({ $tint }) => $tint || '#2aa6b3'};
`
const ActivityMain = styled.div` min-width: 0; display: flex; flex-direction: column; gap: 2px; `
const ActivityText = styled.span` font-size: 13px; color: #1f2937; line-height: 1.4; `
const ActivityMeta = styled.span` font-size: 11px; color: #9aa0aa; `

const UndoMergeBtn = styled.button`
  align-self: flex-start;
  margin-top: 6px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 7px;
  background: #fff;
  color: ${({ theme }) => theme?.colors?.haiti || '#0C1229'};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  &:hover:not(:disabled) { background: #f3f4f6; }
  &:disabled { opacity: 0.6; cursor: default; }
`
const ActivityEmpty = styled.div`
  padding: 24px; text-align: center; color: #9aa0aa; font-size: 13px;
`
const skelPulse = keyframes`
  0% { opacity: 0.55; }
  50% { opacity: 1; }
  100% { opacity: 0.55; }
`
const SkeletonLine = styled.span`
  display: block;
  height: 11px;
  border-radius: 4px;
  background: #ececed;
  animation: ${skelPulse} 1.2s ease-in-out infinite;
`

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`

const Page = styled.div`
  padding: 24px 32px 48px;
  max-width: 1280px;
  margin: 0 auto;
  animation: ${fadeIn} 280ms ease both;
  @media (max-width: 768px) { padding: 16px 12px 40px; }
`

const Head = styled.header`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) { gap: 12px; margin-bottom: 16px; }
`
const HeadText = styled.div`
  display: flex; flex-direction: column; gap: 4px; min-width: 0;

  @media (max-width: 768px) { flex: 1 1 100%; }
`
const Eyebrow = styled.span`
  font-size: 10.5px; letter-spacing: 0.14em; text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]}; font-weight: 800;
`
const Title = styled.h1`
  margin: 0;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 28px;
  letter-spacing: -0.6px;
  color: ${({ theme }) => theme.colors.haiti};
  overflow-wrap: anywhere;

  @media (max-width: 768px) { font-size: 23px; letter-spacing: -0.4px; }
`
const Subtitle = styled.p`
  margin: 0;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.grey[800]};
  max-width: 64ch;
  line-height: 1.5;
`

const SkeletonCard = styled.div`
  min-height: 220px;
  border-radius: 12px;
  background: #f2f2f5;
  border: 1px solid #ececf1;
  animation: ${skelPulse} 1.4s ease-in-out infinite;
`

const Grid = styled.div`
  display: grid;
  /* min() keeps the track from ever exceeding the container — a bare 330px
     minimum overflows any screen narrower than that. */
  grid-template-columns: repeat(auto-fill, minmax(min(330px, 100%), 1fr));
  gap: 14px;

  @media (max-width: 768px) { gap: 10px; }
`

const FilterBar = styled.div`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 14px;

  @media (max-width: 768px) { gap: 6px; }
`
const SearchInput = styled.input`
  flex: 1; min-width: 220px; padding: 8px 12px; font: inherit; font-size: 16px;
  box-sizing: border-box;
  @media (max-width: 768px) { flex: 1 1 100%; min-width: 0; }
  border: 1px solid #ececec; border-radius: 999px; color: #111827; background: #fff;
  &::placeholder { color: #9ca3af; }
  &:focus { outline: none; border-color: #110c2f; }
`
const FilterWrap = styled.div`
  width: 170px; max-width: 100%; min-width: 0;
`
const ClearBtn = styled.button`
  font: inherit;
  border: 1px solid #e0e0e4; background: #fff; color: #6a6f7c;
  border-radius: 999px; padding: 7px 13px; font-size: 12px; font-weight: 700; cursor: pointer;
  &:hover { background: #f4f4f6; color: #110c2f; border-color: #d0d0d6; }
`
const Count = styled.span`
  font-size: 14px; font-weight: 600; color: #4b5563;
  margin-left: auto;
`

const Card = styled.div`
  background: ${({ $inactive }) => ($inactive ? '#fafafb' : '#fff')};
  border: 1px solid #ececec;
  border-radius: 14px;
  padding: 18px 20px;
  display: flex; flex-direction: column; gap: 12px;
  opacity: ${({ $inactive }) => ($inactive ? 0.72 : 1)};
  transition: opacity 0.14s ease, background 0.14s ease;
`

const CardTop = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 10px;
`
const CardIdent = styled.div` display: flex; flex-direction: column; gap: 3px; min-width: 0; `
const CardName = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`
const CardCompany = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[800]};
`
const TradeChip = styled.span`
  align-self: flex-start;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(51,190,204,0.10);
  color: #1f8c97;
  border: 1px solid rgba(51,190,204,0.26);
  margin-top: 2px;
`
const NewlyIdentifiedPill = styled.span`
  align-self: flex-start;
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eef2ff;
  color: #4338ca;
  border: 1px solid #c7d2fe;
  margin-top: 4px;
`
const EditBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  height: 30px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #dcdce0;
  background: #fff;
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 12px; font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.14s ease, border-color 0.14s ease;
  &:hover { background: #f4f4f6; border-color: #c9c9cf; }
`

const DeleteContractorBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  height: 30px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #dcdce0;
  background: #fff;
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 12px; font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.14s ease, border-color 0.14s ease, color 0.14s ease;
  &:hover { background: #fdecea; border-color: #b00020; color: #b00020; }
`

const VettingWrap = styled.div`
  position: relative;
  align-self: flex-start;
  display: inline-flex;
`
const VettingBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 5px 3px 9px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 10px; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase;
  background: ${({ $tint }) => `${$tint}14`};
  color: ${({ $tint }) => $tint};
  border: 1px solid ${({ $tint }) => `${$tint}40`};
  white-space: nowrap;
  transition: background 0.14s ease, border-color 0.14s ease;
  &:hover { background: ${({ $tint }) => `${$tint}22`}; border-color: ${({ $tint }) => `${$tint}66`}; }
`
const VettingBtnDot = styled.span`
  width: 6px; height: 6px; border-radius: 50%;
  background: ${({ $tint }) => $tint};
  flex-shrink: 0;
`
const VettingCaret = styled.span`
  display: inline-flex;
  transition: transform 0.14s ease;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`
const VettingMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 50;
  width: 264px;
  max-width: min(78vw, calc(100vw - 32px));
  background: #fff;
  border: 1px solid #e6e6ea;
  border-radius: 12px;
  box-shadow: 0 8px 26px rgba(20,20,40,0.14);
  padding: 6px;
  display: flex; flex-direction: column; gap: 2px;
`
const VettingMenuHead = styled.div`
  display: flex; flex-direction: column; gap: 3px;
  padding: 6px 8px 5px;
  font-size: 10px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase;
  color: ${({ theme }) => theme.colors.haiti};
  span {
    font-weight: 500; text-transform: none; letter-spacing: 0;
    font-size: 12px; line-height: 1.45;
    color: #3f4453;
  }
`
const VettingMenuItem = styled.button`
  font: inherit;
  text-align: left;
  display: flex; align-items: flex-start; gap: 8px;
  width: 100%;
  padding: 8px 9px;
  border-radius: 9px;
  border: 1px solid ${({ $active }) => ($active ? '#e6e6ea' : 'transparent')};
  background: ${({ $active }) => ($active ? '#f6f6f9' : 'transparent')};
  cursor: pointer;
  transition: background 0.12s ease;
  &:hover { background: #f2f2f6; }
`
const VettingItemDot = styled.span`
  width: 9px; height: 9px; border-radius: 50%;
  background: ${({ $tint }) => $tint};
  margin-top: 3px;
  flex-shrink: 0;
`
const VettingItemText = styled.span` display: flex; flex-direction: column; gap: 2px; min-width: 0; `
const VettingItemLabel = styled.span`
  font-size: 12.5px; font-weight: 700;
  color: ${({ $tint }) => $tint};
  display: inline-flex; align-items: center; gap: 6px;
`
const CurrentTag = styled.span`
  font-size: 9px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase;
  color: #3a4050;
  background: #e2e4ea;
  border-radius: 999px;
  padding: 1px 6px;
`
const VettingItemDesc = styled.span`
  font-size: 12.5px; line-height: 1.45;
  color: #3f4453;
`

const StatusRow = styled.div`
  display: flex; align-items: flex-end; flex-wrap: wrap;
  gap: 16px;
  margin-top: 4px;
`
const StatusField = styled.div`
  display: flex; flex-direction: column; align-items: flex-start; gap: 4px;
`
const StatusCaption = styled.span`
  font-size: 9.5px; font-weight: 800; letter-spacing: 0.07em; text-transform: uppercase;
  color: #5b6270;
`
const ExplainerWrap = styled.div`
  position: relative;
  display: inline-flex;
`
const ExplainerBtn = styled.button`
  font: inherit;
  width: 26px; height: 26px;
  border-radius: 50%;
  border: 1px solid #dcdce0;
  background: #fff;
  color: #5b6270;
  cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background 0.14s ease, color 0.14s ease, border-color 0.14s ease;
  &:hover { background: #f4f4f6; color: ${({ theme }) => theme.colors.haiti}; border-color: #c9c9cf; }
`
const ExplainerPop = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0; left: auto;
  z-index: 50;
  width: 300px;
  max-width: min(82vw, calc(100vw - 32px));
  background: #fff;
  border: 1px solid #e6e6ea;
  border-radius: 12px;
  box-shadow: 0 8px 26px rgba(20,20,40,0.14);
  padding: 12px 13px;
  display: flex; flex-direction: column; gap: 10px;
`
const ExplainerTitle = styled.div`
  font-size: 10px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase;
  color: ${({ theme }) => theme.colors.haiti};
`
const ExplainerItem = styled.div`
  display: flex; flex-direction: column; gap: 2px;
`
const ExplainerHead = styled.span`
  font-size: 12.5px; font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
`
const ExplainerBody = styled.span`
  font-size: 12.5px; line-height: 1.45;
  color: #3f4453;
`
const ExplainerFoot = styled.div`
  font-size: 12px; line-height: 1.45;
  color: #3f4453;
  border-top: 1px solid #eeeef1;
  padding-top: 9px;
`

const CardActions = styled.div`
  display: flex; align-items: center; gap: 6px; flex-shrink: 0;
`
const StatusToggle = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 9px 4px 8px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 10.5px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(21,128,61,0.28)' : '#dcdce0')};
  background: ${({ $active }) => ($active ? 'rgba(21,128,61,0.08)' : '#f4f4f6')};
  color: ${({ $active }) => ($active ? '#15803d' : '#8A8F9C')};
  transition: all 0.14s ease;
  &:hover {
    filter: brightness(0.97);
    border-color: ${({ $active }) => ($active ? 'rgba(21,128,61,0.45)' : '#c9c9cf')};
  }
`
const StatusDot = styled.span`
  width: 7px; height: 7px; border-radius: 50%;
  background: ${({ $active }) => ($active ? '#15803d' : '#b4b4bb')};
`
const SourceStrip = styled.div`
  font: inherit;
  text-align: left;
  display: inline-flex; align-items: center; gap: 8px; align-self: flex-start;
  padding: 6px 11px;
  border-radius: 9px;
  background: #f5f2fc;
  border: 1px solid #7C5FE022;
  color: #6a54c4;
  font-size: 11.5px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: background 0.14s ease, border-color 0.14s ease;
  svg { color: #7C5FE0; flex-shrink: 0; }
  &:hover {
    ${({ $clickable }) => ($clickable ? 'background: #efe9fb; border-color: #7C5FE044;' : '')}
  }
`
const SourceStripText = styled.span`
  strong { font-weight: 800; color: #5b45b0; }
  em { font-style: normal; font-weight: 700; color: #5b45b0; }
`
const RecentTag = styled.span`
  flex-shrink: 0;
  font-size: 9px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase;
  padding: 2px 7px; border-radius: 999px;
  background: rgba(21,128,61,0.12); color: #15803d; border: 1px solid rgba(21,128,61,0.28);
`
const PendingHint = styled.div`
  display: flex; align-items: center; gap: 7px;
  padding: 7px 11px;
  border-radius: 9px;
  background: #fef8ec;
  border: 1px solid #f3e2bf;
  color: #92600a;
  font-size: 11.5px; line-height: 1.4;
  strong { font-weight: 800; color: #7a4f06; }
  svg { flex-shrink: 0; }
`

const BlockedStrip = styled.div`
  display: flex; align-items: flex-start; gap: 8px;
  padding: 8px 12px;
  background: rgba(204,34,0,0.07);
  border: 1px solid rgba(204,34,0,0.28);
  color: #b00020;
  border-radius: 8px;
  font-size: 11.5px;
`
const BlockedBody = styled.div`
  display: flex; flex-direction: column; gap: 2px;
  strong { font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; font-size: 10.5px; }
  span { font-weight: 500; }
`

const DocCover = styled.span`
  margin-left: 6px;
  font-size: 10px;
  font-weight: 800;
  color: #1f8c97;
`

const FootBtns = styled.div` display: inline-flex; flex-wrap: wrap; gap: 6px; `

const PickerBack = styled.button`
  font: inherit; background: none; border: none; cursor: pointer;
  align-self: flex-start; padding: 0 0 6px;
  font-size: 12px; font-weight: 700; color: #2aa6b3;
  &:hover { text-decoration: underline; }
`
const PickerSearch = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 8px 11px; border: 1px solid #e3e6eb; border-radius: 9px;
`
const PickerSearchInput = styled.input`
  font: inherit; flex: 1; min-width: 0; border: none; outline: none; background: transparent;
  font-size: 16px; color: #1d2433;
`
const PickerList = styled.div`
  margin-top: 8px; display: flex; flex-direction: column; gap: 5px;
  max-height: 240px; overflow-y: auto;
`
const PickerSkeleton = styled.div`
  height: 46px; border-radius: 9px; background: #f1f5f9;
  animation: ${skelPulse} 1.4s ease-in-out infinite;
`
const PickerEmpty = styled.div`
  padding: 18px; text-align: center; font-size: 12.5px; color: #94a3b8;
`
const PickerRow = styled.button`
  font: inherit; text-align: left; cursor: pointer; width: 100%;
  display: flex; align-items: center; gap: 9px;
  padding: 9px 11px; border-radius: 9px;
  border: 1px solid ${({ $active }) => ($active ? '#2aa6b3' : '#eceef2')};
  background: ${({ $active }) => ($active ? 'rgba(42,166,179,0.06)' : '#fff')};
  &:hover { border-color: #d3d7de; }
`
const PickerRowText = styled.span` flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; `
const PickerRowName = styled.span`
  font-size: 12.5px; font-weight: 600; color: #1d2433;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`
const PickerRowMeta = styled.span`
  font-size: 10.5px; color: #94a3b8;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`
const PickerRadio = styled.span`
  width: 15px; height: 15px; border-radius: 50%; flex-shrink: 0;
  border: 2px solid ${({ $active }) => ($active ? '#2aa6b3' : '#cbd5e1')};
  background: ${({ $active }) => ($active ? '#2aa6b3' : '#fff')};
  box-shadow: ${({ $active }) => ($active ? 'inset 0 0 0 2px #fff' : 'none')};
`

const AttendanceList = styled.div`
  display: flex; flex-direction: column; gap: 7px;
  max-height: 280px;
  overflow-y: auto;
`
const AttendanceRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: ${({ $editing }) => ($editing ? '11px 12px' : '8px 10px')};
  background: ${({ $editing }) => ($editing ? '#fff' : '#fafbfc')};
  border: 1px solid ${({ $editing }) => ($editing ? '#dcdfe6' : '#f0f0f3')};
  border-radius: 10px;
`
const RowActions = styled.div` display: inline-flex; align-items: center; gap: 7px; flex-shrink: 0; `
const EditRowBody = styled.div`
  flex: 1; min-width: 0;
  display: flex; flex-direction: column; gap: 9px;
`
const EditTimes = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 9px;
  @media (max-width: 480px) { grid-template-columns: minmax(0, 1fr); }
`
const EditField = styled.div` display: flex; flex-direction: column; gap: 4px; `
const EditLabel = styled.span`
  font-size: 9.5px; letter-spacing: 0.07em; text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]}; font-weight: 800;
`
const EditInput = styled.input`
  font: inherit;
  padding: 8px 10px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  font-size: 12.5px;
  background: #fff;
  color: ${({ theme }) => theme.colors.haiti};
  outline: none;
  transition: border-color 140ms ease, box-shadow 140ms ease;
  &:focus { border-color: #33becc; box-shadow: 0 0 0 3px rgba(51,190,204,0.15); }
`
const EditActions = styled.div` display: inline-flex; gap: 8px; justify-content: flex-end; `
const AttendanceMain = styled.div` flex: 1; display: flex; flex-direction: column; gap: 1px; min-width: 0; `
const AttendanceBuilding = styled.span`
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`
const AttendanceMeta = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[800]};
`
const AttendanceNote = styled.span`
  font-size: 11px;
  font-style: italic;
  color: ${({ theme }) => theme.colors.grey[800]};
`
const UnitChips = styled.div`
  display: flex; flex-wrap: wrap; gap: 4px;
  margin: 2px 0 1px;
`
const UnitChip = styled.span`
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ $tint }) => $tint};
  color: #fff;
  white-space: nowrap;
`
const MultiUnitSelect = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #dcdfe6;
  border-radius: 10px;
  background: #f8f9fb;
  padding: 8px 12px;
`
const UnitCheckboxRow = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 4px 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  label { cursor: pointer; }
`
const CheckboxInput = styled.input`
  margin-top: 0;
  accent-color: #2aa6b3;
  width: 16px; height: 16px;
  cursor: pointer;
`
const UnitSelectDivider = styled.div`
  border-bottom: 1px solid #ececf0;
  margin: 4px 0;
`
const UnitSelectedCount = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[800]};
  margin-top: 4px;
`
const UnitsLoading = styled.div`
  font-size: 11.5px;
  font-style: italic;
  color: ${({ theme }) => theme.colors.grey[800]};
  padding: 8px 2px;
`

const AlertStrip = styled.div`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 11px;
  background: rgba(204,34,0,0.07);
  border: 1px solid rgba(204,34,0,0.26);
  color: #b00020;
  border-radius: 8px;
  font-size: 11.5px;
  font-weight: 800;
`

const FolderTile = styled.button`
  font: inherit; text-align: left; cursor: pointer; width: 100%;
  display: flex; align-items: center; gap: 11px;
  padding: 10px 12px; border-radius: 10px;
  border: 1px solid #ececf1; background: #fafbfc;
  transition: border-color .12s ease, background .12s ease;
  &:hover { border-color: #d3d7de; background: #f4f6f8; }
`
const FolderTileIcon = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 34px; height: 34px; flex-shrink: 0; border-radius: 8px;
  background: #eef2ff; color: #4338ca;
`
const FolderTileText = styled.span` flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; `
const FolderTileTitle = styled.span`
  font-size: 12.5px; font-weight: 700; color: ${({ theme }) => theme.colors.haiti};
`
const FolderTileMeta = styled.span` font-size: 11px; color: ${({ theme }) => theme.colors.grey[800]}; `
const FolderTileRight = styled.span` display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0; `
const FolderChip = styled.span`
  display: inline-flex; align-items: center;
  padding: 2px 8px; border-radius: 999px;
  font-size: 10px; font-weight: 800; letter-spacing: 0.02em;
  background: ${({ $bg }) => $bg}; color: ${({ $fg }) => $fg};
  white-space: nowrap;
`
const FolderGroup = styled.div` display: flex; flex-direction: column; gap: 5px; margin-bottom: 14px; `
const FolderGroupHead = styled.div`
  display: flex; align-items: center; gap: 7px;
  font-size: 11px; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  margin-bottom: 2px;
`
const FolderGroupDot = styled.span`
  width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
  background: ${({ $tint }) => $tint};
`
const FolderGroupCount = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px; border-radius: 999px;
  background: #eef1f5; color: ${({ theme }) => theme.colors.grey[800]};
  font-size: 10px; font-weight: 800;
`
const FolderEmpty = styled.div`
  display: flex; flex-direction: column; align-items: center; text-align: center;
  gap: 8px; padding: 30px 20px;
`
const FolderEmptyTitle = styled.div`
  font-size: 14px; font-weight: 700; color: ${({ theme }) => theme.colors.haiti};
`
const FolderEmptyText = styled.div`
  font-size: 12.5px; line-height: 1.5; max-width: 340px;
  color: ${({ theme }) => theme.colors.grey[800]};
`
const DocRow = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto auto;
  gap: 8px;
  align-items: center;
  padding: 6px 8px;
  background: ${({ $expired }) => $expired ? 'rgba(204,34,0,0.05)' : '#fafbfc'};
  border: 1px solid ${({ $expired }) => $expired ? 'rgba(204,34,0,0.22)' : '#f0f0f3'};
  border-radius: 8px;
  @media (max-width: 480px) {
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-auto-rows: auto;
    row-gap: 4px;
  }
`
const DocBadges = styled.span` display: inline-flex; align-items: center; gap: 4px; `
const AutoDocTag = styled.span`
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 9px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase;
  padding: 2px 6px; border-radius: 999px;
  background: #eef2ff; color: #4338ca; border: 1px solid #c7d2fe;
  white-space: nowrap;
`
const DocType = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 999px;
  background: ${({ $tint }) => `${$tint}14`};
  color: ${({ $tint }) => $tint};
  border: 1px solid ${({ $tint }) => `${$tint}33`};
  white-space: nowrap;
`
const DocName = styled.span`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.haiti};
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`
const DocExpiry = styled.span`
  font-size: 10.5px;
  font-weight: 700;
  color: ${({ $expired, $soon, theme }) => $expired ? '#b00020' : $soon ? '#d97706' : theme.colors.grey[800]};
  white-space: nowrap;
`
const DocDelete = styled.button`
  font: inherit;
  width: 20px; height: 20px;
  border: none; background: transparent;
  color: ${({ theme }) => theme.colors.grey[800]};
  cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 5px;
  &:hover { background: rgba(204,34,0,0.08); color: #b00020; }
`

const CardFoot = styled.footer`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  border-top: 1px dashed #ececec;
  padding-top: 10px;
`
const PerfRow = styled.div` display: flex; flex-wrap: wrap; gap: 10px 14px; `
const PerfStat = styled.span` display: inline-flex; flex-direction: column; align-items: center; gap: 1px; `
const PerfValue = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 18px;
  letter-spacing: -0.3px;
  color: ${({ $tint, theme }) => $tint || theme.colors.haiti};
  line-height: 1;
  font-variant-numeric: tabular-nums;
`
const PerfLabel = styled.span`
  font-size: 9px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 700;
`
const PerfHint = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-style: italic;
`
const AddDocBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 4px;
  padding: 5px 11px;
  background: transparent;
  border: 1px dashed #cbcbd2;
  color: ${({ theme }) => theme.colors.grey[800]};
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
  &:hover { border-color: #33becc; color: #1f8c97; }
`

const Empty = styled.div`
  padding: 40px;
  text-align: center;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  color: ${({ theme }) => theme.colors.grey[800]};
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  p { margin: 0; font-size: 13px; max-width: 48ch; line-height: 1.6; }
`

const PrimaryBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 10px 18px;
  background: #2aa6b3;
  color: #fff;
  border: none;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 800;
  cursor: pointer;
  transition: background 150ms ease;
  &:hover:not(:disabled) { background: #239aa6; }
  &:disabled {
    background: #e7e9ee;
    color: #a9adb7;
    cursor: not-allowed;
  }
`
const HeadActions = styled.div`
  display: inline-flex; align-items: center; gap: 10px; flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    gap: 8px;
    button { flex: 1 1 auto; justify-content: center; min-height: 40px; }
  }
`
const MergeBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 10px 16px;
  background: #fff;
  color: #1f8b98;
  border: 1px solid #cfe7ea;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 800;
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease;
  &:hover { background: #e9f6f7; border-color: #2aa6b3; }
`
const GhostBtn = styled.button`
  font: inherit;
  padding: 10px 18px;
  background: #fff;
  border: 1px solid #dcdfe6;
  color: ${({ theme }) => theme.colors.haiti};
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  transition: background 140ms ease, border-color 140ms ease;
  &:hover:not(:disabled) { background: #f4f5f7; border-color: #c7cbd4; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const backdropIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`
const modalIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`

const Backdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(12, 18, 41, 0.42);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
  z-index: 1400;
  animation: ${backdropIn} 160ms ease both;
  @media (max-width: 560px) { padding: 0; align-items: flex-end; }
`
const ModalCard = styled.div`
  background: #fff;
  border: 1px solid #eceef2;
  border-radius: 20px;
  width: min(580px, 100%);
  max-height: calc(100vh - 48px);
  display: flex; flex-direction: column;
  overflow: hidden;
  box-shadow: 0 16px 44px rgba(12, 18, 41, 0.16);
  animation: ${modalIn} 220ms ease both;
  @media (max-width: 560px) {
    border-radius: 20px 20px 0 0;
    max-height: 92vh;
    width: 100%;
  }
`
const ModalHead = styled.header`
  display: flex; align-items: flex-start; gap: 13px;
  padding: 20px 22px 16px;
  border-bottom: 1px solid #f0f1f4;
`
const ModalIcon = styled.span`
  flex-shrink: 0;
  width: 40px; height: 40px;
  border-radius: 11px;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(51,190,204,0.12);
  color: #1f8c97;
  border: 1px solid rgba(51,190,204,0.26);
`
const ModalHeadText = styled.div` display: flex; flex-direction: column; gap: 3px; min-width: 0; flex: 1; `
const ModalSub = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.grey[800]};
`
const CloseBtn = styled.button`
  font: inherit;
  flex-shrink: 0;
  width: 30px; height: 30px;
  margin: -2px -4px 0 0;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.grey[800]};
  cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background 140ms ease, color 140ms ease;
  &:hover { background: #f1f2f5; color: ${({ theme }) => theme.colors.haiti}; }
`
const ModalTitle = styled.h3`
  margin: 0;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 21px;
  letter-spacing: -0.4px;
  color: ${({ theme }) => theme.colors.haiti};
`
const ModalBody = styled.div`
  padding: 18px 22px 22px;
  overflow-y: auto;
  display: flex; flex-direction: column; gap: 14px;
`
const ModalFoot = styled.footer`
  display: flex; gap: 10px; justify-content: flex-end; align-items: center;
  padding: 14px 22px;
  border-top: 1px solid #f0f1f4;
  background: #fbfbfc;
`
const SectionLabel = styled.div`
  font-size: 10px; letter-spacing: 0.13em; text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]}; font-weight: 800;
  display: flex; align-items: center; gap: 10px;
  margin-top: 2px;
  &::after {
    content: ''; flex: 1; height: 1px;
    background: #ececf0;
  }
`
const Required = styled.span` color: #b00020; font-weight: 800; `

const ExtractBtn = styled.button`
  font: inherit; display: inline-flex; align-items: center; gap: 5px;
  margin-top: 7px; padding: 5px 11px; border-radius: 8px;
  font-size: 12px; font-weight: 700; cursor: pointer;
  color: #4a4f5e; background: #f1f3f7; border: 1px solid #e6e8ec;
  transition: background 140ms ease;
  &:hover:not(:disabled) { background: #e9ecf1; }
  &:disabled { opacity: 0.55; cursor: wait; }
  svg { color: #7c5fe0; }
`

const VettingHint = styled.p`
  margin: 10px 0 0; font-size: 12px; line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const VettingDetails = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  margin: 2px 0 12px; padding: 12px 14px;
  background: #fafbfc; border: 1px solid #eef0f3; border-radius: 10px;
`
const VettingItem = styled.div`
  display: flex; align-items: baseline; justify-content: space-between; gap: 12px;
  font-size: 12.5px;
`
const VettingKey = styled.span`
  color: ${({ theme }) => theme.colors.grey[800]}; font-weight: 600; white-space: nowrap;
`
const VettingVal = styled.span`
  color: ${({ theme }) => theme.colors.haiti}; font-weight: 700; text-align: right;
`

const PrefillZone = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  padding: 14px 16px;
  margin-bottom: 18px;
  background: #f6fbfa;
  border: 1px solid #e6f3f1;
  border-radius: 12px;
`
const PrefillBody = styled.div` min-width: 0; display: flex; flex-direction: column; gap: 3px; `
const PrefillTitle = styled.span` font-size: 13px; font-weight: 800; color: #1f2937; `
const PrefillText = styled.span` font-size: 12px; line-height: 1.45; color: #6b7280; `
const PrefillUpload = styled.label`
  flex-shrink: 0;
  display: inline-flex; align-items: center;
  padding: 8px 14px;
  background: #2aa6b3;
  color: #fff;
  border-radius: 999px;
  font-size: 12.5px; font-weight: 800;
  cursor: pointer;
  &:hover { filter: brightness(1.05); }
`
const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 13px;
  @media (max-width: 560px) { grid-template-columns: minmax(0, 1fr); }
`
const Field = styled.div`
  display: flex; flex-direction: column; gap: 5px;
  ${({ $span }) => $span ? 'grid-column: 1 / -1;' : ''}
`
const FieldLabel = styled.label`
  font-size: 10.5px; letter-spacing: 0.08em; text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]}; font-weight: 800;
`
const fieldBase = `
  font: inherit;
  padding: 11px 13px;
  border: 1px solid #dcdfe6;
  border-radius: 10px;
  font-size: 13.5px;
  background: #f8f9fb;
  outline: none;
  transition: border-color 140ms ease, box-shadow 140ms ease, background 140ms ease;
  &::placeholder { color: #aeb2bd; }
  &:hover { border-color: #c7cbd4; }
  &:focus { border-color: #33becc; background: #fff; box-shadow: 0 0 0 3px rgba(51,190,204,0.15); }
`
const Input = styled.input`
  ${fieldBase}
  color: ${({ theme }) => theme.colors.haiti};
`
const TextArea = styled.textarea`
  ${fieldBase}
  color: ${({ theme }) => theme.colors.haiti};
  resize: vertical;
  min-height: 56px;
  line-height: 1.5;
`
const ActiveRow = styled.div`
  display: inline-flex; align-items: center; gap: 9px;
  padding: 11px 13px;
  border: 1px solid #ededf1;
  border-radius: 10px;
  background: #fafbfc;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  input { width: 16px; height: 16px; accent-color: #2aa6b3; cursor: pointer; }
  label { cursor: pointer; }
`
const SignInPanel = styled.div`
  display: flex; flex-direction: column; gap: 13px;
  padding: 15px;
  border: 1px solid #eceef2;
  border-radius: 14px;
  background: #f8fafb;
`
const SignInAction = styled.div` display: flex; justify-content: flex-end; `
const OnSiteTag = styled.span`
  flex-shrink: 0;
  font-size: 9.5px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase;
  padding: 3px 9px;
  border-radius: 999px;
  background: rgba(42,166,179,0.12);
  color: #1f8c97;
  border: 1px solid rgba(42,166,179,0.30);
`
